// 3rd code snippet
import { API_URL } from "@/constant";
import type { RootState } from "@/store";
import { addChatMessage, addOrReplacePeer, addWhiteboardEvent, removePeer, resetCallState, setActiveSpeakerInfo, setActiveSpeakerStream, setAvailableCameras, setAvailableMics, setConnectionStatus, setIsCallActive, setIsTokenAcquired, setIsWhiteboardOpen, setLobbyStream, setLocalMicOn, setLocalStream, setLocalVideoOn, setScreenShareActive, setSelectedCamera, setSelectedMic, setUserId } from "@/store/slices/callSlice";
import type { SignalRNegotiateResponse } from "@/store/slices/signalR";
import axios from "axios";
import Cookies from "js-cookie";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

// Define missing types if not already defined
export interface PeerData {
    id: string;
    stream: MediaStream | null;
    status: 'connecting' | 'connected' | 'disconnected' | 'failed';
}

export interface Participant {
    id: string;
    name: string;
    avatar: string;
    isMuted: boolean;
    isVideoOff: boolean;
    cameraStream: MediaStream | null;
    screenShareStream: MediaStream | null;
    isScreenSharing: boolean;
}
const MESSAGE_TYPE = {
    SESSIONEND: "SESSION_END",
    CHAT: "CHAT_MESSAGE",
};
export interface UseSignalRWebRTCHook {
    connect: (roomId: string, userName: string) => void;
    disconnect: () => void;
    sendMessage: (message: string) => void;
    sendRtcChatMessage: (message: string) => void;
    sendRtcDataMessage: (messageType: string, data: any) => void;
    toggleLocalMic: () => void;
    toggleLocalVideo: () => void;
    toggleScreenShare: () => Promise<void>;
    handleDeviceChange: (deviceId: string, type: "audio" | "video") => Promise<void>;
    handleFacingModeChange: (facingMode: "user" | "environment") => void;
    areStreamsReady: Boolean;
    InteractionData : any;
    EstimedWaitTime : number;
    abortSignalRConnection: () => void;
}

declare global {
    interface Window {
        RTCMultiConnection: any;
    }
}

const HUB_NAME = "myHub";

export const useSignalRWebRTC = (socketURL: string | null, navigate: (path: string) => void, onNewChatMessageReceivedFromRTC?: () => void): UseSignalRWebRTCHook => {
    const dispatch = useDispatch();

    // Select state directly from the Redux store
    const localStream = useSelector((state: RootState) => state.call.localStream);
    const lobbyStream = useSelector((state: RootState) => state.call.lobbyStream);
    const peers = useSelector((state: RootState) => state.call.peers);
    const connectionStatus = useSelector((state: RootState) => state.call.connectionStatus);
    const isCallActive = useSelector((state: RootState) => state.call.isCallActive);
    const userId = useSelector((state: RootState) => state.call.userId);
    // const chatMessages = useSelector((state: RootState) => state.call.chatMessages);
    const localMicOn = useSelector((state: RootState) => state.call.localMicOn);
    const localVideoOn = useSelector((state: RootState) => state.call.localVideoOn);
    const screenShareActive = useSelector((state: RootState) => state.call.screenShareActive);
    const selectedMic = useSelector((state: RootState) => state.call.selectedMic);
    const selectedCamera = useSelector((state: RootState) => state.call.selectedCamera);
    const availableMics = useSelector((state: RootState) => state.call.availableMics);
    const availableCameras = useSelector((state: RootState) => state.call.availableCameras);
    const isTokenAcquired = useSelector((state: RootState) => state.call.isTokenAcquired);
    const activeSpeakerInfo = useSelector((state: RootState) => state.call.activeSpeakerInfo);
    const activeSpeakerStream = useSelector((state: RootState) => state.call.activeSpeakerStream);
    const [interactionData, setInteractionData] = useState<any>(null);
    const [EstimedWaitTime, setEstimedWaitTime] = useState<number>(0);
    // const isWhiteboardOpen = useSelector((state: RootState) => state.call.isWhiteboardOpen); // NEW: Select whiteboard visibility
    // const chatMessages = useSelector((state: RootState) => state.call.chatMessages); // Uncomment if needed for direct use

    const currentUserName = Cookies.get("ServerSessionID");
    const UserDetails = localStorage.getItem("customerConnect")
    const sessionData = JSON.parse(UserDetails || "{}");
    const UserName = sessionData.values?.first_name
    // Ref to store the user-provided name from the connect call
    const userProvidedNameRef = useRef<string | null>(null);

    // Refs for SignalR and RTCMultiConnection instances
    const reconnectWsRef = useRef<WebSocket | null>(null); // Main SignalR WebSocket
    const rtcmConnectionRef = useRef<any | null>(null); // RTCMultiConnection instance
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const localScreenShareStreamRef = useRef<MediaStream | null>(null); // Ref for local screen share stream

    const hydrationTimerRef = useRef<NodeJS.Timeout | null>(null);
const isRenegotiatingRef = useRef(false);

    // SignalR specific refs
    const connectionTokenRef = useRef<string | null>(null);
    const connectionIdRef = useRef<string | null>(null);
    const roomIdRef = useRef<string | null>(null); // Stores the current room ID
    const agentEndedSessionRef = useRef(false);
    const agentUserIdRef = useRef<string | null>(null);
    // Ref to hold the latest peers state for use in event handlers
    const latestPeersRef = useRef(peers);

    // pion server
    // let pionConnected = false;
    // let pionPC: RTCPeerConnection | null = null;
    // let pionWS: WebSocket | null = null;
    // let pionOfferSent = false;


    useEffect(() => {
        latestPeersRef.current = peers;
    }, [peers]);

    // Local participant info (derived, but useful for RTCMultiConnection)
    const localParticipant: Participant = useMemo(() => ({
        id: userId || UserName, // Use userId if available, else currentUserName from cookie
        name: UserName,
        avatar: "/avatars/01.png",
        isMuted: !localMicOn,
        isVideoOff: !localVideoOn,
        cameraStream: localStream, // Now specifically cameraStream
        screenShareStream: localScreenShareStreamRef.current, // Use ref for local screen share stream
        isScreenSharing: screenShareActive,
    }), [userId, UserName, localMicOn, localVideoOn, localStream, screenShareActive]);

    // Derived state for stream readiness (still derived here, but based on Redux state)
    const areStreamsReady = useMemo(() => {
        // Streams are ready if localStream is available AND the local user's entry in 'peers' has its cameraStream.
        if (!userId || !localStream) {
            console.log("DEBUG: areStreamsReady: False (userId or localStream missing)");
            return false;
        }
        // IMPORTANT: Use the userId from Redux, which should now match RTCMultiConnection's local ID
        const localUserInPeers = peers.find(p => p.id === userId);
        const ready = localUserInPeers !== undefined && localUserInPeers.cameraStream !== null;
        console.log(`DEBUG: areStreamsReady: ${ready} (localUserInPeers: ${!!localUserInPeers}, localUserInPeers.cameraStream: ${!!localUserInPeers?.cameraStream})`);
        return ready;
    }, [localStream, userId, peers]);

    // --- Device Enumeration ---
    const enumerateDevices = useCallback(async () => {
        // Crucial for cross-device compatibility: Check if navigator.mediaDevices is available.
        // It might be undefined in non-secure contexts (HTTP) or older/restricted environments.
        if (!navigator.mediaDevices) {
            console.warn("DEBUG: navigator.mediaDevices is not available. Cannot enumerate devices.");
            dispatch(addChatMessage({ sender: "System", message: "Media devices not accessible. Please ensure you are on a secure connection (HTTPS) and grant permissions.", timestamp: Date.now() }));
            return;
        }

        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const audioInputs = devices.filter(d => d.kind === 'audioinput');
            const videoInputs = devices.filter(d => d.kind === 'videoinput');

            // Map MediaDeviceInfo to SerializableDeviceInfo before dispatching
            dispatch(setAvailableMics(audioInputs.map(d => ({
                deviceId: d.deviceId,
                kind: d.kind,
                label: d.label,
                groupId: d.groupId
            }))));
            dispatch(setAvailableCameras(videoInputs.map(d => ({
                deviceId: d.deviceId,
                kind: d.kind,
                label: d.label,
                groupId: d.groupId
            }))));
            console.log("DEBUG: Enumerated devices. Mics:", audioInputs, "Cameras:", videoInputs);

            // *** FIX: Auto-select available camera and mic if not already selected ***
            // This ensures selectedMic/Camera are populated at the earliest possible moment.
            // Essential for mobile where default devices might be the only option or need explicit selection.
            if (!selectedMic && audioInputs.length > 0) {
                dispatch(setSelectedMic(audioInputs[0].deviceId));
                console.log("DEBUG: Auto-selected default mic:", audioInputs[0].label || audioInputs[0].deviceId);
            }
            if (!selectedCamera && videoInputs.length > 0) {
                dispatch(setSelectedCamera(videoInputs[0].deviceId));
                console.log("DEBUG: Auto-selected default camera:", videoInputs[0].label || videoInputs[0].deviceId);
            }

        } catch (error) {
            console.error("DEBUG: Error enumerating devices:", error);
            // Provide user feedback if device enumeration fails
            dispatch(addChatMessage({ sender: "System", message: `Error enumerating devices: ${error instanceof Error ? error.message : String(error)}.`, timestamp: Date.now() }));
        }
    }, [dispatch, selectedMic, selectedCamera]);

    useEffect(() => {
        // Ensure navigator.mediaDevices exists before adding event listeners to prevent errors.
        if (!navigator.mediaDevices) {
            console.warn("DEBUG: navigator.mediaDevices is not available. Skipping devicechange event listener setup.");
            return;
        }
        enumerateDevices();
        navigator.mediaDevices.addEventListener('devicechange', enumerateDevices);
        return () => {
            navigator.mediaDevices.removeEventListener(
                "devicechange",
                enumerateDevices
            );
        };
    }, [enumerateDevices]);

    // --- Lobby Stream Acquisition (for initial media access before full connection) ---
    useEffect(() => {
        const getLobbyMediaStream = async () => {
            // Essential check for media device availability before attempting to get user media.
            if (!navigator.mediaDevices) {
                console.warn("DEBUG: navigator.mediaDevices is not available. Cannot acquire lobby stream.");
                dispatch(setConnectionStatus('failed'));
                dispatch(addChatMessage({ sender: "System", message: "Media devices not accessible. Please ensure you are on a secure connection (HTTPS) and grant permissions.", timestamp: Date.now() }));
                return;
            }

            if (!lobbyStream) { // Only get lobby stream if not already acquired in Redux
                try {
                    console.log("DEBUG: Attempting to acquire lobby media stream...");
                    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
                    dispatch(setLobbyStream({
                        id: 'lobby',
                        name: 'Lobby',
                        avatar: '',
                        isMuted: false,
                        isVideoOff: false,
                        cameraStream: stream,
                        screenShareStream: null,
                        isScreenSharing: false,
                    }));
                    console.log("DEBUG: Lobby media stream acquired:", stream);
                    console.log("DEBUG: Lobby stream tracks - Audio:", stream.getAudioTracks(), "Video:", stream.getVideoTracks());
                } catch (error) {
                    console.error("DEBUG: Error acquiring lobby media stream:", error);
                    dispatch(setConnectionStatus('failed'));
                    // Provide specific user feedback for common media errors
                    if (error instanceof DOMException) {
                        if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
                            dispatch(addChatMessage({ sender: "System", message: "Media access denied. Please grant camera and microphone permissions.", timestamp: Date.now() }));
                        } else if (error.name === "NotFoundError") {
                            dispatch(addChatMessage({ sender: "System", message: "No camera or microphone found. Please connect one.", timestamp: Date.now() }));
                        } else {
                            dispatch(addChatMessage({ sender: "System", message: `Media access error: ${error.message}.`, timestamp: Date.now() }));
                        }
                    } else {
                        dispatch(addChatMessage({ sender: "System", message: "An unknown error occurred while accessing media. Please try again.", timestamp: Date.now() }));
                    }
                }
            }
        };

        getLobbyMediaStream();

        return () => {
            // Important: Ensure lobby stream tracks are stopped when component unmounts or stream is replaced.
            // This prevents resource leaks, especially on mobile.
            if (lobbyStream) {
                lobbyStream.cameraStream?.getTracks().forEach(track => track.stop());
                console.log("DEBUG: Lobby stream tracks stopped on cleanup.");
            }
        };
    }, [dispatch, lobbyStream]);

    // --- Local Media Stream Acquisition/Update ---
    const getLocalMedia = useCallback(async (
        requestAudio: boolean, // Should we request audio?
        requestVideo: boolean, // Should we request video?
        audioDeviceId?: string,
        videoDeviceId?: string,
        facingMode?: "user" | "environment"
    ) => {
        // Essential check for media device availability.
        if (!navigator.mediaDevices) {
            console.warn("DEBUG: navigator.mediaDevices is not available. Cannot acquire local media.");
            dispatch(setLocalMicOn(false));
            dispatch(setLocalVideoOn(false));
            dispatch(addChatMessage({ sender: "System", message: "Media devices not accessible. Please ensure you are on a secure connection (HTTPS) and grant permissions.", timestamp: Date.now() }));
            return null;
        }

        try {
            console.log("DEBUG: Attempting to acquire/update local media stream with constraints:", { requestAudio, requestVideo, audioDeviceId, videoDeviceId, facingMode });

            const audioConstraints: MediaTrackConstraints | boolean = requestAudio ? (audioDeviceId ? { deviceId: { exact: audioDeviceId } } : true) : false;
            let videoConstraints: MediaTrackConstraints | boolean = false;

            if (requestVideo) {
                if (videoDeviceId) {
                    videoConstraints = { deviceId: { exact: videoDeviceId } };
                } else if (facingMode) {
                    videoConstraints = { facingMode: facingMode };
                } else {
                    videoConstraints = true; // Request any video device
                }
            }

            const constraints: MediaStreamConstraints = {
                audio: audioConstraints,
                video: videoConstraints,
            };

            // If neither audio nor video is requested, getUserMedia will fail.
            // Request at least audio (muted) to get a stream object.
            if (!constraints.audio && !constraints.video) {
                console.warn("DEBUG: Neither audio nor video requested. Requesting audio: true to get a stream object.");
                constraints.audio = true; // Request a default audio stream to avoid getUserMedia failure
            }


            const stream = await navigator.mediaDevices.getUserMedia(constraints);

            // Set track enabled status based on the requested state (requestAudio/requestVideo)
            stream.getAudioTracks().forEach(track => track.enabled = requestAudio);
            stream.getVideoTracks().forEach(track => track.enabled = requestVideo);

            // Update selected device IDs in Redux based on what was actually acquired
            if (requestAudio && stream.getAudioTracks().length > 0) {
                dispatch(setSelectedMic(stream.getAudioTracks()[0].getSettings().deviceId || ""));
            } else if (!requestAudio) {
                dispatch(setSelectedMic("")); // Clear selected mic if audio is turned off
            }

            if (requestVideo && stream.getVideoTracks().length > 0) {
                dispatch(setSelectedCamera(stream.getVideoTracks()[0].getSettings().deviceId || ""));
            } else if (!requestVideo) {
                dispatch(setSelectedCamera("")); // Clear selected camera if video is turned off
            }

            // Handle stream replacement in RTCMultiConnection
            if (rtcmConnectionRef.current) {
                console.log("DEBUG: RTCMultiConnection instance exists. Handling local stream update.");

                // Remove old local camera stream if it exists and is different from the new one
                if (localStream && localStream !== stream) {
                    console.log("DEBUG: Stopping and removing old local camera stream from RTCMultiConnection:", localStream);
                    localStream.getTracks().forEach(track => track.stop());
                    rtcmConnectionRef.current.removeStream(localStream);
                }

                // Add the new stream to RTCMultiConnection
                
                rtcmConnectionRef.current.addStream(stream);
                              
                console.log("DEBUG: Added new local stream to RTCMultiConnection:", stream);
            }

            dispatch(setLocalStream(stream)); // Dispatch action to update Redux store (camera stream)
            console.log("DEBUG: Local media acquired/updated and dispatched to Redux:", stream);

            return stream;
        } catch (err) {
            console.error("DEBUG: Error accessing camera/microphone in getLocalMedia:", err);
            // Handle specific errors for better user experience
            if (!(err instanceof OverconstrainedError)) {
                // If it's not an OverconstrainedError (which means constraints couldn't be met, but media might still be available with different settings)
                // then it's a more serious failure to get media.
                dispatch(setLocalMicOn(false));
                dispatch(setLocalVideoOn(false));
            }
            // Provide user feedback for media acquisition errors
            if (err instanceof DOMException) {
                if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
                    dispatch(addChatMessage({ sender: "System", message: "Media access denied. Please grant camera and microphone permissions.", timestamp: Date.now() }));
                } else if (err.name === "NotFoundError") {
                    dispatch(addChatMessage({ sender: "System", message: "No camera or microphone found. Please connect one.", timestamp: Date.now() }));
                } else {
                    dispatch(addChatMessage({ sender: "System", message: `Media access error: ${err.message}.`, timestamp: Date.now() }));
                }
            } else {
                dispatch(addChatMessage({ sender: "System", message: "An unknown error occurred while accessing media. Please try again.", timestamp: Date.now() }));
            }
            dispatch(setLocalStream(null)); // Explicitly set to null if getLocalMedia fails
            return null;
        }
    }, [dispatch, localStream, rtcmConnectionRef]);

    useEffect(() => {
        getLocalMedia(true, true, selectedMic, selectedCamera, "user")
    }, []);

    // --- SignalR Message Sending (for primary SignalR WebSocket) ---
    const sendSignalRMessage = useCallback((message: any) => { // Type 'any' for SignalRHubMessage for now
        if (reconnectWsRef.current && reconnectWsRef.current.readyState === WebSocket.OPEN && connectionTokenRef.current) {
            const wrappedMessage = {
                C: connectionTokenRef.current,
                M: [message],
                I: Math.floor(Math.random() * 1000000)
            };
            reconnectWsRef.current.send(JSON.stringify(wrappedMessage));
            console.log("DEBUG: Sent SignalR message via Reconnect WS:", wrappedMessage);
        } else {
            console.warn("DEBUG: SignalR Reconnect WebSocket not open or connection token missing, cannot send message:", message);
            dispatch(addChatMessage({ sender: "System", message: "Chat message failed to send: Not connected to server.", timestamp: Date.now() }));
        }
    }, [dispatch]);

    // --- Polling for GetInteractionData ---
    const PostGetInteractionData = useCallback(async () => {
        const requestId = localStorage.getItem("requestID") || '';
        if (!requestId) {
            console.warn('DEBUG: GetInteractionData: No requestID found in localStorage. Skipping poll.');
            return;
        }
        try {
            const response = await fetch(API_URL.POST_GETINTERACTION, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ requestId: requestId }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log('DEBUG: POST GetInteractionData:', result);
            setInteractionData(result.d.PositionInQueue)
            setEstimedWaitTime(result.d.EstimatedWaitTime)

            console.log("interaction",result.d.PositionInQueue, result.d.EstimatedWaitTime )


        } catch (error) {
            console.error('DEBUG: Error posting to GetInteractionData:', error);
            dispatch(addChatMessage({ sender: "System", message: "Failed to update interaction status with server.", timestamp: Date.now() }));
        }
    }, [dispatch]);

    // --- Send Chat Message Logic (via SignalR) ---
    const sendMessage = useCallback((message: string) => {
        if (connectionStatus === 'connected' && reconnectWsRef.current && roomIdRef.current) {
            sendSignalRMessage({
                H: HUB_NAME,
                M: 'sendMessage',
                A: [currentUserName, roomIdRef.current, message]
            });
        } else {
            console.warn("DEBUG: Not connected (via main SignalR WS) or no room ID to send chat message.");
            dispatch(addChatMessage({ sender: "System", message: "Cannot send chat message: Not connected to the call.", timestamp: Date.now() }));
        }
    }, [connectionStatus, sendSignalRMessage, currentUserName, dispatch]);

    // NEW: Function to send chat messages via RTCMultiConnection's data channel
    const sendRtcChatMessage = useCallback((message: string) => {
        // Ensure rtcmConnectionRef.current exists and is connected before sending
        if (rtcmConnectionRef.current) {
            console.log("DEBUG: sendRtcChatMessage: rtcmConnectionRef.current exists. Current 'is' status:", rtcmConnectionRef.current.is);

            // Check for 'connected' status OR if there are any active participants
            const isConnectedViaRTC = rtcmConnectionRef.current.is === 'connected' || (rtcmConnectionRef.current.getAllParticipants && rtcmConnectionRef.current.getAllParticipants().length > 0);

            if (isConnectedViaRTC) {
                // Changed messageType for the socket event to "messages" to align with the old code's socket.on('messages') listener.
                // The data payload itself still indicates "chat" as its internal messageType.
                rtcmConnectionRef.current.semit({
                    data: message,
                    messageType: "chat" // Internal message type for the payload
                }, null, null, "messages"); // The 4th argument specifies the socket event name
                console.log("DEBUG: RTCMultiConnection - Sent chat message via SignalR socket (event 'messages'):", message);
            } else {
                const statusDetail = rtcmConnectionRef.current.is !== undefined ? `Status: ${rtcmConnectionRef.current.is}` : "Status: undefined";
                const participantsCount = rtcmConnectionRef.current.getAllParticipants ? rtcmConnectionRef.current.getAllParticipants().length : "N/A";
                console.warn(`DEBUG: RTCMultiConnection not ready for chat. Current 'is' status: '${rtcmConnectionRef.current.is}', Participants: ${participantsCount}. Cannot send chat message via data channel.`);
                dispatch(addChatMessage({ sender: "System", message: `Cannot send real-time chat: Not connected to video call. ${statusDetail}.`, timestamp: Date.now() }));
            }
        } else {
            console.warn("DEBUG: RTCMultiConnection instance is null, cannot send chat message via data channel.");
            dispatch(addChatMessage({ sender: "System", message: "Cannot send real-time chat: Video call connection not initialized.", timestamp: Date.now() }));
        }
    }, [dispatch]);

    // NEW: Generic function to send data via RTCMultiConnection's data channel
    const sendRtcDataMessage = useCallback((messageType: string, data: any) => {
        if (rtcmConnectionRef.current && (rtcmConnectionRef.current.is === 'connected' || (rtcmConnectionRef.current.getAllParticipants && rtcmConnectionRef.current.getAllParticipants().length > 0))) {
            const payload = {
                messageType: messageType, // e.g., 'whiteboard_event', 'open_whiteboard', 'close_whiteboard'
                data: data, // This `data` is the second argument to sendRtcDataMessage, which for whiteboard will contain the FabricObjectData or {action, data}
                // senderId: userId, // Include senderId for whiteboard events
                // remoteUserId: localStorage.getItem("requestID") || '' // Include requestId for server-side tracking
            };
            rtcmConnectionRef.current.semit(payload, null, "messages"); // The 4th argument specifies the socket event name
            console.log(`DEBUG: RTCMultiConnection - Sent data channel message (type: ${messageType}):`, payload);
        } else {
            console.warn(`DEBUG: RTCMultiConnection not ready. Cannot send data channel message (type: ${messageType}).`);
            dispatch(addChatMessage({ sender: "System", message: `Cannot send ${messageType} data: Video call connection not initialized or no active participants.`, timestamp: Date.now() }));
        }
    }, [dispatch, userId]);

//     async function connectPionRecordingCustomer(RoomId: string) {
//     if (pionConnected) return;

// const wsUrl =
//         `ws://localhost:8443/ws` +
//         `?interactionId=${RoomId}` +
//         `&token=${localStorage.getItem("InteractionToken") || ""}`;

//       pionWS = new WebSocket(wsUrl);
//     pionPC = new RTCPeerConnection({
//       iceServers: [{
//               urls: ["turn:cvcp.csinfocomm.com:3478?transport=tcp"],
//               "username": "admin", "credential": "pass@123"
//             }],
//     });

//     pionPC.onicecandidate = (e) => {
//         if (e.candidate && pionWS?.readyState === WebSocket.OPEN) {
//             pionWS.send(JSON.stringify({
//                 type: "ice",
//                 data: e.candidate
//             }));
//         }
//     };

//     pionWS.onopen = () => {
//         pionWS!.send(JSON.stringify({
//             type: "join",
//             data: {
//                 room: RoomId,
//                 id: "customer",
//                 role: 'customer'
//             }
//         }));
//         pionConnected = true;
//     };

//     pionWS.onmessage = async (ev) => {
//         const msg = JSON.parse(ev.data);

//         if (msg.type === "answer") {
//             await pionPC!.setRemoteDescription({
//                 type: "answer",
//                 sdp: msg.data.sdp
//             });
//         }

//         if (msg.type === "server-offer") {
//             await pionPC!.setRemoteDescription({
//                 type: "offer",
//                 sdp: msg.data.sdp
//             });

//             const ans = await pionPC!.createAnswer();
//             await pionPC!.setLocalDescription(ans);

//             pionWS!.send(JSON.stringify({
//                 type: "answer",
//                 data: { type: "answer", sdp: ans.sdp }
//             }));
//         }

//         if (msg.type === "ice") {
//             await pionPC!.addIceCandidate(msg.data);
//         }
//     };
// }


    // --- Main SignalR Connect Logic ---
    const connect = useCallback(async (roomId: string, userName: string) => {
        if (connectionStatus === 'connecting' || connectionStatus === 'connected') {
            console.warn("DEBUG: Already connecting or connected, ignoring new connect call.");
            return;
        }

        userProvidedNameRef.current = userName; // Store the user-provided name here

        // Ensure lobby stream is available before attempting to connect to SignalR
        if (!lobbyStream) {
            console.warn("DEBUG: Lobby stream not available. Cannot connect to SignalR.");
            dispatch(setConnectionStatus('failed'));
            dispatch(addChatMessage({ sender: "System", message: "Media devices not accessible. Please ensure you are on a secure connection (HTTPS) and grant permissions.", timestamp: Date.now() }));
            return;
        }
        dispatch(setConnectionStatus('connecting'));
        // Initial userId set, will be updated by RTCMultiConnection's actual ID
        dispatch(setUserId(userName));
        roomIdRef.current = roomId;

        dispatch(addChatMessage({ sender: "System", message: "Initiating connection...", timestamp: Date.now() }));
        dispatch(setIsCallActive(false));
        dispatch(setIsTokenAcquired(false));
        console.log("DEBUG: Initiating SignalR connection for room:", roomId, "user:", userName);

        try {
            const connectionDataPayload = JSON.stringify([{ name: HUB_NAME }]);

            console.log("DEBUG: Starting SignalR negotiate...");
            const negotiateParams = {
                clientProtocol: '2.1',
                username: currentUserName,
                connectionData: connectionDataPayload,
                _: Date.now()
            };

            const negotiateResponse = await axios.get<SignalRNegotiateResponse>(API_URL.NEGOTIATE_URL, {
                params: negotiateParams,
                withCredentials: true,
            });

            const { Url, ConnectionToken, ConnectionId, TryWebSockets } = negotiateResponse.data;
            console.log("DEBUG: SignalR negotiate response:", negotiateResponse.data);


            if (!TryWebSockets) {
                console.error("DEBUG: Server does not support WebSockets transport. This demo requires WebSockets.");
                dispatch(setConnectionStatus('failed'));
                dispatch(addChatMessage({ sender: "System", message: "Server does not support WebSockets. Cannot connect.", timestamp: Date.now() }));
                return;
            }

            connectionTokenRef.current = ConnectionToken;
            connectionIdRef.current = ConnectionId;
            console.log("DEBUG: Negotiate successful. ConnectionToken:", ConnectionToken, "ConnectionId:", ConnectionId);
            console.log("DEBUG: Calling SignalR /start endpoint...");

            const startParams = {
                transport: 'webSockets',
                clientProtocol: '2.1',
                username: currentUserName,
                connectionToken: ConnectionToken,
                connectionData: connectionDataPayload,
                _: Date.now() + 10
            };

            const startResponse = await axios.get(API_URL.START_URL, {
                params: startParams,
                withCredentials: true,
            });
            console.log("DEBUG: SignalR /start endpoint called successfully. Response:", startResponse.data);


            const baseSignalrOrigin = new URL(API_URL.NEGOTIATE_URL).origin;

            // Close existing SignalR WebSocket before opening new one
            if (reconnectWsRef.current && reconnectWsRef.current.readyState === WebSocket.OPEN) {
                console.log("DEBUG: Closing existing SignalR WebSocket.");
                reconnectWsRef.current.close();
                reconnectWsRef.current = null;
            }

            const wsProtocol = baseSignalrOrigin.startsWith('https://') ? 'wss://' : 'ws://';
            const wsOrigin = baseSignalrOrigin.replace(/^https?:\/\//, ''); // Remove http:// or https://

            const finalSignalRWebSocketUrl = `${wsProtocol}${wsOrigin}${Url}/connect?transport=webSockets&clientProtocol=2.1&connectionToken=${encodeURIComponent(ConnectionToken)}&username=${currentUserName}&connectionData=${encodeURIComponent(connectionDataPayload)}&tid=${Math.floor(Math.random() * 11)}`;

            console.log("DEBUG: Attempting SignalR WebSocket connection to:", finalSignalRWebSocketUrl);
            const ws = new WebSocket(finalSignalRWebSocketUrl);
            reconnectWsRef.current = ws; // Assign to reconnectWsRef for consistency

            ws.onopen = () => {
                console.log("DEBUG: SignalR WebSocket connected!");
                dispatch(setConnectionStatus('connected'));
                console.log("DEBUG: Dispatched setConnectionStatus('connected'). Current Redux connectionStatus:", connectionStatus); // Log after dispatch
                dispatch(setIsTokenAcquired(true)); // Dispatch action to set token acquired
                console.log("DEBUG: Dispatched setIsTokenAcquired(true). Current Redux isTokenAcquired:", isTokenAcquired); // Log after dispatch
                dispatch(addChatMessage({ sender: "System", message: "Connected to call server. Waiting for agent...", timestamp: Date.now() }));
            };

            ws.onmessage = async (event) => {
                const rawMessage = event.data;
                console.log("DEBUG: SignalR Main WS Raw Message:", rawMessage); // Log raw message
                let parsedMessage;
                try {
                    // Attempt to parse as a standard SignalR message
                    parsedMessage = JSON.parse(rawMessage);
                    console.log("DEBUG: SignalR Main WS Parsed as standard message:", parsedMessage);

                    if (Array.isArray(parsedMessage.M)) {
                        for (const hubMessage of parsedMessage.M) {
                            if (hubMessage && typeof hubMessage.H === 'string' && typeof hubMessage.M === 'string') {
                                if (hubMessage.H.toLowerCase() === HUB_NAME.toLowerCase()) {
                                    switch (hubMessage.M) {
                                        case 'UserAssigned':
                                            console.log("DEBUG: >>> Detected 'UserAssigned' message <<<");
                                            if (pollingIntervalRef.current) {
                                                clearInterval(pollingIntervalRef.current);
                                                pollingIntervalRef.current = null;
                                                console.log("DEBUG: Stopped GetInteractionData polling due to UserAssigned.");
                                            }

                                            const [assignedRoomId, assignedUser, status] = hubMessage.A;
                                            console.log(`DEBUG: UserAssigned - RoomId: ${assignedRoomId}, User: ${assignedUser}, Status: ${status}`);

                                            if (!assignedRoomId || !assignedUser || status !== "success") {
                                                console.warn("DEBUG: UserAssigned message received but critical data missing or status not 'success'. Skipping API calls and UI switch.");
                                                dispatch(addChatMessage({ sender: "System", message: "Failed to get agent assignment details.", timestamp: Date.now() }));
                                                return;
                                            }

                                            roomIdRef.current = assignedRoomId;
                                            localStorage.setItem("AgentName", assignedUser);

                                            console.log(`DEBUG: Sending 'joinRoom' message for assigned room: ${assignedRoomId} via SignalR WS`);
                                            sendSignalRMessage({
                                                H: HUB_NAME,
                                                M: 'joinRoom',
                                                A: [assignedRoomId, currentUserName]
                                            });

                                            try {
                                                const reqId = localStorage.getItem("requestID");
                                                console.log("DEBUG: RequestID from localStorage for UpdateRoomStatus:", reqId);
                                                if (reqId) {
                                                    const updateRoomStatusResponse = await axios.post(
                                                        API_URL.UPDATE_ROOM_STATUS,
                                                        { requestId: reqId, roomid: assignedRoomId, extn: null, status: "Connected" },
                                                        { headers: { 'Content-Type': 'application/json' } }
                                                    );
                                                    console.log("DEBUG: UpdateRoomStatus successful:", updateRoomStatusResponse.data);
                                                } else {
                                                    console.warn("DEBUG: No requestId found in localStorage for UpdateRoomStatus API call. Skipping.");
                                                }
                                            } catch (error) {
                                                toast.error("Error calling UpdateRoomStatus API")
                                                console.error("DEBUG: Error calling UpdateRoomStatus API:", error);
                                                // dispatch(addChatMessage({ sender: "System", message: "Failed to update room status.", timestamp: Date.now() }));
                                            }

                                            try {
                                                const reqId = localStorage.getItem("requestID");
                                                console.log("DEBUG: RequestID from localStorage for Token API:", reqId);
                                                if (reqId && assignedUser) {
                                                    const tokenServerUrl = `${API_URL.TOKEN_SERVER}?request_id=${reqId}&username=${assignedUser}&role=customer`;
                                                    console.log("DEBUG: Calling VCCStatServer/token with URL:", tokenServerUrl);
                                                    const tokenServerResponse = await axios.post(tokenServerUrl);
                                                    console.log("DEBUG: VCCStatServer/token successful:", tokenServerResponse.data);
                                                    dispatch(setIsCallActive(true)); // Dispatch action to set call active
                                                    console.log("DEBUG: Dispatched setIsCallActive(true). Current Redux isCallActive:", isCallActive); // Log after dispatch
                                                    const interactionToken = tokenServerResponse?.data?.Data;
                                                    localStorage.setItem("InteractionToken", interactionToken);

                                                    console.log("DEBUG: setIsCallActive(true) called. isCallActive should now be true.");

                                                    console.log("DEBUGGER: InteractionToken obtained:", interactionToken);

                                                    // --- IMPORTANT: Ensure selectedMic and selectedCamera have values before saving ---
                                                    // This is the key change to ensure proper device persistence.
                                                    const micToSave = selectedMic || (availableMics.length > 0 ? availableMics[0].deviceId : '');
                                                    const cameraToSave = selectedCamera || (availableCameras.length > 0 ? availableCameras[0].deviceId : '');

                                                    console.log("DEBUG: Saving to localStorage - micToSave:", micToSave, "cameraToSave:", cameraToSave);

                                                    // --- Initialize and connect RTCMultiConnection here ---
                                                    // Access RTCMultiConnection from the global window object
                                                    const RTCMultiConnectionGlobal = window.RTCMultiConnection; // Access from window
                                                    if (typeof RTCMultiConnectionGlobal !== 'undefined') {
                                                        console.log("DEBUG: Initializing RTCMultiConnection with its own Socket.IO connection...");
                                                        // Pass the iceServers configuration to the RTCMultiConnection constructor
                                                        const connection = new RTCMultiConnectionGlobal(assignedRoomId, null, null, interactionToken);

                                                        connection.iceServers = [];//ignore all default
                                                        connection.iceServers = window.AtHomeConfig.rtcConfig.iceServers;
                                                        // connection.candidates = { bundlePolicy: null }
                                                        rtcmConnectionRef.current = connection;
                                                        console.log("DEBUG: RTCMultiConnection instance created. Initial 'is' status:", connection.is);

                                                        // *** IMPORTANT FIX: Update Redux userId with RTCMultiConnection's actual userid ***
                                                        dispatch(setUserId(connection.userid));
                                                        console.log("DEBUG: Redux userId updated to RTCMultiConnection's userid:", connection.userid);

                                                        // Store call session info in localStorage, INCLUDING media preferences
                                                        localStorage.setItem('callSession', JSON.stringify({
                                                            roomId: assignedRoomId,
                                                            userId: connection.userid,
                                                            isCallActive: true,
                                                            localMicOn: localMicOn,
                                                            localVideoOn: localVideoOn,
                                                            selectedMic: micToSave,
                                                            selectedCamera: cameraToSave,
                                                            timestamp: Date.now()
                                                        }));

                                                        // This is crucial for React to manage video elements directly.
                                                        connection.autoCreateMediaElement = false;
                                                        console.log("DEBUG: RTCMultiConnection.autoCreateMediaElement set to false.");

                                                        // Set RTCMultiConnection's socketURL directly
                                                        connection.socketURL = socketURL;
                                                        console.log("DEBUG: RTCMultiConnection socketURL set to:", connection.socketURL);

                                                        // Configure RTCMultiConnection session
                                                        connection.session = {
                                                            audio: true,
                                                            video: true,
                                                            data: true
                                                        };
                                                        connection.sdpConstraints.mandatory = {
                                                            OfferToReceiveAudio: true,
                                                            OfferToReceiveVideo: true
                                                        };

                                                        // Pass the interactionToken to RTCMultiConnection's extra data
                                                        connection.extra = {
                                                            userName: UserName,
                                                            interactionToken: interactionToken
                                                        };
                                                        connection.onUserStatusChanged = (event: any) => {
                                                            console.log("DEBUG: RTCMultiConnection onUserStatusChanged:", event);
                                                            if (agentEndedSessionRef.current) return;

                                                            const newAgentId = event.userid;
                                                            const agentName = event?.extra?.userFullName || "Agent";

                                                            // 🚫 Ignore everything if agent already ended session
                                                            //   if (agentEndedSessionRef.current) return;

                                                            if (event.status === "offline") {
                                                            console.log("⚠️ Agent went offline (temporary). Showing reconnect UI.");
                                                            toast.info(`Agent ${agentName} disconnected. Reconnecting...`);
                                                            dispatch(setConnectionStatus("ActiveConnecting"));
                                                            }

                                                            if (
                                                                agentUserIdRef.current &&
                                                                agentUserIdRef.current !== newAgentId
                                                            ) {
                                                                console.log(
                                                                "🧹 Removing stale agent peer:",
                                                                agentUserIdRef.current
                                                                );
                                                                dispatch(removePeer(agentUserIdRef.current));
                                                            }

                                                            agentUserIdRef.current = newAgentId;

                                                            // 🔥 Reset active speaker so new stream can bind
                                                            dispatch(setActiveSpeakerInfo(null));
                                                            dispatch(setActiveSpeakerStream(null));

                                                            dispatch(setConnectionStatus("connected"));

                                                            // 🔥 FORCE renegotiation so onstream fires again
                                                            // setTimeout(() => {
                                                            //     try {
                                                            //     connection.renegotiate(newAgentId);
                                                            //     } catch (e) {
                                                            //     console.warn("Renegotiation failed:", e);
                                                            //     }
                                                            // }, 300);
                                                        };
                                                        // --- DEBUGGER: Check InteractionToken in RTCMultiConnection.extra ---
                                                        console.log("DEBUG: RTCMultiConnection initialized with extra data:", connection.extra);

                                                        
                                                        // Set up RTCMultiConnection event listeners
                                                        connection.onstream = (event: any) => {
                                                            console.log("DEBUG: RTCMultiConnection onstream event received:", event);
                                                            console.log("AgentName:", event.userid, event.extra.userFullName)
                                                            // *** REFINED: More robust screen stream detection ***
                                                            const isScreenStream = event.stream.isScreen || // RTCMultiConnection specific flag
                                                                event.stream.getVideoTracks().some((track: MediaStreamTrack) => {
                                                                    const label = track.label.toLowerCase();
                                                                    const isLabelBased = label.includes('screen') || label.includes('display');
                                                                    const settings = track.getSettings();
                                                                    // Check for common properties indicating a display source
                                                                    const isDisplaySurface = settings.displaySurface === 'monitor' ||
                                                                        settings.displaySurface === 'window' ||
                                                                        settings.displaySurface === 'application';
                                                                    return isLabelBased || isDisplaySurface;
                                                                });
                                                            console.log(`DEBUG: Stream from ${event.userid} detected as Screen Stream: ${isScreenStream}`);

                                                            const existingPeer = latestPeersRef.current.find(p => p.id === event.userid);
                                                            const peerName = event.extra.userFullName ;
                                                            const peerAvatar = existingPeer?.avatar || "/avatars/02.png";

                                                            // Initialize updated properties based on existing peer state
                                                            let updatedCameraStream = existingPeer?.cameraStream || null;
                                                            let updatedScreenShareStream = existingPeer?.screenShareStream || null;
                                                            let updatedIsScreenSharing = existingPeer?.isScreenSharing || false;
                                                            let updatedIsMuted = existingPeer?.isMuted || !event.stream.getAudioTracks().some((t: MediaStreamTrack) => t.enabled);
                                                            let updatedIsVideoOff = existingPeer?.isVideoOff || !event.stream.getVideoTracks().some((t: MediaStreamTrack) => t.enabled);


                                                            if (event.type === 'local') {
                                                                if (isScreenStream) {
                                                                    localScreenShareStreamRef.current = event.stream; // Store in ref
                                                                    dispatch(setScreenShareActive(true)); // Update local flag
                                                                    updatedScreenShareStream = event.stream;
                                                                    updatedIsScreenSharing = true;
                                                                    // For local screen share, camera stream remains localStream (from Redux)
                                                                    updatedCameraStream = localStream; // Use Redux localStream for local camera
                                                                    updatedIsVideoOff = !localVideoOn; // This should reflect the camera's video status
                                                                    updatedIsMuted = !localMicOn; // This should reflect the camera's mic status
                                                                    console.log("DEBUG: Local screen share stream set.");
                                                                } else { // Local camera stream
                                                                    dispatch(setLocalStream(event.stream)); // This already updates localStream (camera)
                                                                    updatedCameraStream = event.stream;
                                                                    updatedIsVideoOff = !event.stream.getVideoTracks().some((t: MediaStreamTrack) => t.enabled);
                                                                    updatedIsMuted = !event.stream.getAudioTracks().some((t: MediaStreamTrack) => t.enabled);
                                                                    // Local screen share status and stream should be retained from refs/Redux
                                                                    updatedScreenShareStream = localScreenShareStreamRef.current;
                                                                    updatedIsScreenSharing = screenShareActive; // Use Redux screenShareActive for local
                                                                    console.log("DEBUG: Local camera stream and peers for local user should now be updated.");
                                                                }
                                                            } else if (event.type === 'remote') {
                                                                if (isScreenStream) {
                                                                    updatedScreenShareStream = event.stream;
                                                                    updatedIsScreenSharing = true;
                                                                    // For remote screen share, don't change cameraStream state if it exists
                                                                    // Use existingPeer's cameraStream for retention
                                                                    updatedCameraStream = existingPeer?.cameraStream || null;
                                                                    updatedIsVideoOff = false; // Screen share implies video is not off
                                                                    // Mute status for screen share (audio) should come from stream if available, or keep existing for consistency
                                                                    updatedIsMuted = !event.stream.getAudioTracks().some((t: MediaStreamTrack) => t.enabled); // Check screen share stream's audio
                                                                    console.log(`DEBUG: Remote screen share stream from ${event.userid} set.`);
                                                                } else { // Remote camera stream
                                                                    updatedCameraStream = event.stream;
                                                                    updatedIsVideoOff = !event.stream.getVideoTracks().some((t: MediaStreamTrack) => t.enabled);
                                                                    updatedIsMuted = !event.stream.getAudioTracks().some((t: MediaStreamTrack) => t.enabled);
                                                                    // For remote camera, don't change screenShareStream state if it exists
                                                                    // Use existingPeer's screenShareStream for retention
                                                                    updatedScreenShareStream = existingPeer?.screenShareStream || null;
                                                                    updatedIsScreenSharing = existingPeer?.isScreenSharing || false; // Retain existing screen sharing status
                                                                    console.log(`DEBUG: Remote camera stream from ${event.userid} set.`);
                                                                }
                                                            }
                                                            const stalePeers = latestPeersRef.current.filter(
                                                             p => p.name === peerName && p.id !== event.userid
                                                            );

                                                            stalePeers.forEach(p => {
                                                            console.log("🧹 Removing stale peer from Redux:", p.id);
                                                            dispatch(removePeer(p.id));
                                                            });

                                                            // Disp                                                atch addPeer with the calculated updated streams and statuses
                                                            // This ensures the Redux 'peers' array is always up-to-date with the latest stream info.
                                                            dispatch(addOrReplacePeer({
                                                                id: event.userid,
                                                                name: peerName,
                                                                avatar: peerAvatar,
                                                                isMuted: updatedIsMuted,
                                                                isVideoOff: updatedIsVideoOff,
                                                                cameraStream: updatedCameraStream,
                                                                screenShareStream: updatedScreenShareStream,
                                                                isScreenSharing: updatedIsScreenSharing,
                                                                status: 'connected'
                                                            }));

                                                            // Active speaker logic:
                                                            // Prioritize screen share, then active camera streams.
                                                            if (isScreenStream && (!activeSpeakerInfo || !activeSpeakerInfo.isScreenSharing)) {
                                                                dispatch(setActiveSpeakerInfo({
                                                                    id: event.userid,
                                                                    name: peerName,
                                                                    avatar: peerAvatar,
                                                                    isMuted: updatedIsMuted,
                                                                    isVideoOff: updatedIsVideoOff,
                                                                    cameraStream: updatedCameraStream,
                                                                    screenShareStream: updatedScreenShareStream,
                                                                    isScreenSharing: updatedIsScreenSharing,
                                                                }));
                                                                dispatch(setActiveSpeakerStream(event.stream));
                                                                console.log(`DEBUG: Set active speaker to screen share from ${event.userid}.`);
                                                            } else if (!isScreenStream && (!activeSpeakerInfo || (!activeSpeakerInfo.isScreenSharing && activeSpeakerInfo.id !== event.userid))) {
                                                                // Only set as active speaker if no screen share is active or this is a new camera speaker
                                                                dispatch(setActiveSpeakerInfo({
                                                                    id: event.userid,
                                                                    name: peerName,
                                                                    avatar: peerAvatar,
                                                                    isMuted: updatedIsMuted,
                                                                    isVideoOff: updatedIsVideoOff,
                                                                    cameraStream: updatedCameraStream,
                                                                    screenShareStream: updatedScreenShareStream,
                                                                    isScreenSharing: updatedIsScreenSharing,
                                                                }));
                                                                dispatch(setActiveSpeakerStream(event.stream));
                                                                console.log(`DEBUG: Set active speaker to camera from ${event.userid}.`);
                                                            }
                                                        };

                                                        connection.onstreamended = (event: any) => {
                                                            console.log("DEBUG: RTCMultiConnection onstreamended event:", event);

                                                            // *** REFINED: Use the same robust detection for ending streams ***
                                                            const isScreenStream = event.stream.isScreen ||
                                                                event.stream.getVideoTracks().some((track: MediaStreamTrack) => {
                                                                    const label = track.label.toLowerCase();
                                                                    const settings = track.getSettings();
                                                                    const isLabelBased = label.includes('screen') || label.includes('display');
                                                                    const isDisplaySurface = settings.displaySurface === 'monitor' ||
                                                                        settings.displaySurface === 'window' ||
                                                                        settings.displaySurface === 'application';
                                                                    return isLabelBased || isDisplaySurface;
                                                                });
                                                            console.log(`DEBUG: Stream from ${event.userid} ended, detected as Screen Stream: ${isScreenStream}`);

                                                            // Use latestPeersRef to get the most up-to-date peer state
                                                            const existingPeer = latestPeersRef.current.find(p => p.id === event.userid);
                                                            if (!existingPeer) {
                                                                console.warn(`DEBUG: onstreamended: Peer ${event.userid} not found in state.`);
                                                                return;
                                                            }

                                                            let updatedCameraStream = existingPeer.cameraStream;
                                                            let updatedScreenShareStream = existingPeer.screenShareStream;
                                                            let updatedIsScreenSharing = existingPeer.isScreenSharing;
                                                            let updatedIsMuted = existingPeer.isMuted;
                                                            let updatedIsVideoOff = existingPeer.isVideoOff;

                                                            if (event.type === 'local') {
                                                                if (isScreenStream) {
                                                                    localScreenShareStreamRef.current = null;
                                                                    dispatch(setScreenShareActive(false));
                                                                    updatedScreenShareStream = null;
                                                                    updatedIsScreenSharing = false;
                                                                    // Keep camera stream and its status as is
                                                                    updatedCameraStream = localStream; 
                                                                    updatedIsMuted = !localMicOn;
                                                                    updatedIsVideoOff = !localVideoOn;
                                                                    console.log("DEBUG: Local screen share stream ended.");
                                                                } else { 
                                                                    dispatch(setLocalStream(null)); 
                                                                    updatedCameraStream = null; 
                                                                    updatedIsVideoOff = true; 
                                                                    // Keep screen share stream and its status as is
                                                                    updatedScreenShareStream = localScreenShareStreamRef.current;
                                                                    updatedIsScreenSharing = screenShareActive;
                                                                    console.log("DEBUG: Local camera stream ended (device unplugged or stream stopped).");
                                                                }
                                                            } else if (event.type === 'remote') {
                                                                if (isScreenStream) {
                                                                    updatedScreenShareStream = null;
                                                                    updatedIsScreenSharing = false;
                                                                    // Keep existing camera stream and its status
                                                                    updatedCameraStream = existingPeer.cameraStream;
                                                                    updatedIsMuted = existingPeer.isMuted;
                                                                    updatedIsVideoOff = existingPeer.isVideoOff;
                                                                    console.log(`DEBUG: Remote screen share stream from ${event.userid} ended.`);
                                                                } else { // Remote camera stream ended
                                                                    updatedCameraStream = null;
                                                                    updatedIsVideoOff = true;
                                                                    updatedScreenShareStream = existingPeer.screenShareStream;
                                                                    updatedIsScreenSharing = existingPeer.isScreenSharing;
                                                                    updatedIsMuted = existingPeer.isMuted;
                                                                    console.log(`DEBUG: Remote camera stream from ${event.userid} ended.`);
                                                                }
                                                            }

                                                            // Dispatch addPeer with the calculated updated streams and statuses
                                                            dispatch(addOrReplacePeer({
                                                                id: event.userid,
                                                                name: existingPeer.name,
                                                                avatar: existingPeer.avatar,
                                                                isMuted: updatedIsMuted,
                                                                isVideoOff: updatedIsVideoOff,
                                                                cameraStream: updatedCameraStream,
                                                                screenShareStream: updatedScreenShareStream,
                                                                isScreenSharing: updatedIsScreenSharing,
                                                                status: 'connected'
                                                            }));

                                                            // Active speaker logic:
                                                            // If the ended stream belonged to the active speaker, re-evaluate active speaker.
                                                            if (activeSpeakerInfo?.id === event.userid && activeSpeakerStream === event.stream) { // Check for exact stream match
                                                                let newActiveSpeaker: Participant | null = null;
                                                                let newActiveStream: MediaStream | null = null;

                                                                // Re-evaluate active speaker based on current state of peers
                                                                // Get the *latest* state of the peer whose stream just ended (after it's been updated in Redux)
                                                                const peerAfterUpdate = latestPeersRef.current.find(p => p.id === event.userid);

                                                                // Priority 1: If the *same user* whose screen share just ended now has an.
                                                                if (isScreenStream && peerAfterUpdate && peerAfterUpdate.cameraStream && !peerAfterUpdate.isVideoOff) {
                                                                    newActiveSpeaker = peerAfterUpdate;
                                                                    newActiveStream = peerAfterUpdate.cameraStream;
                                                                    console.log(`DEBUG: Active speaker re-evaluated: Switched to camera of user ${event.userid} after screen share ended.`);
                                                                }
                                                                // Priority 2: Local Screen Share (if active)
                                                                else if (localScreenShareStreamRef.current && screenShareActive) {
                                                                    newActiveSpeaker = { ...localParticipant, name: "Your Screen", isScreenSharing: true };
                                                                    newActiveStream = localScreenShareStreamRef.current;
                                                                    console.log("DEBUG: Active speaker re-evaluated: Switched to Local Screen Share.");
                                                                }
                                                                // Priority 3: Remote Screen Share (if any other is active)
                                                                else {
                                                                    const remoteScreenSharingPeer = latestPeersRef.current.find(p => p.isScreenSharing && p.screenShareStream && p.id !== event.userid);
                                                                    if (remoteScreenSharingPeer) {
                                                                        newActiveSpeaker = remoteScreenSharingPeer;
                                                                        newActiveStream = remoteScreenSharingPeer.screenShareStream;
                                                                        console.log(`DEBUG: Active speaker re-evaluated: Switched to Remote Screen Share from ${remoteScreenSharingPeer.id}.`);
                                                                    }
                                                                    // Priority 4: Local Camera (if active)
                                                                    else if (localStream && localVideoOn) {
                                                                        newActiveSpeaker = localParticipant;
                                                                        newActiveStream = localStream;
                                                                    }
                                                                    // Priority 5: Remote Camera (if any other is active)
                                                                    else {
                                                                        const remoteCameraPeer = latestPeersRef.current.find(p => p.cameraStream && !p.isVideoOff);
                                                                        if (remoteCameraPeer) {
                                                                            newActiveSpeaker = remoteCameraPeer;
                                                                            newActiveStream = remoteCameraPeer.cameraStream;
                                                                        }
                                                                    }
                                                                }
                                                                dispatch(setActiveSpeakerInfo(newActiveSpeaker));
                                                                dispatch(setActiveSpeakerStream(newActiveStream));
                                                                console.log(`DEBUG: Active speaker re-evaluated after peer disconnected. New active speaker: ${newActiveSpeaker?.name || 'None'}`);
                                                            }
                                                        };
                                                        // Add onstatechange listener for more detailed debugging
                                                        connection.onstatechange = (state: any) => {
                                                            console.log(`DEBUG: RTCMultiConnection state changed: ${state.name} (is: ${connection.is})`);
                                                            if (state.name === 'connected') {
                                                                console.log("DEBUG: RTCMultiConnection is now 'connected'.");
                                                            }
                                                        };

                                                        if (localStream) {
                                                            console.log("DEBUG: Re-attaching local stream after reconnect");

                                                            connection.attachStreams.forEach((s: any) => {
                                                                connection.removeStream(s.streamid);
                                                            });

                                                            connection.addStream(localStream);

                                                            // 🔥 Force renegotiation for all peers
                                                            // connection.getAllParticipants().forEach((pid: string) => {
                                                            //     connection.renegotiate(pid);
                                                            // });
                                                        }
                                                        // Get local media and add to RTCMultiConnection
                                                        // console.log("DEBUG: Calling getLocalMedia to acquire local stream for RTCMultiConnection.");
                                                        // Open or join the room
                                                        connection.openOrJoin(assignedRoomId, (isRoomOpened: boolean, event: any) => {
                                                            console.log(`DEBUG: RTCMultiConnection openOrJoin callback. isRoomOpened: ${isRoomOpened}, event:`, event);
                                                            console.log("DEBUG: RTCMultiConnection current 'is' status inside openOrJoin callback:", connection.is);
                                                            setTimeout(() => {
                                                             const participants = connection.getAllParticipants();

                                                             participants.forEach((pid: string) => {
                                                                 console.log("DEBUG: Checking remote streams for participant:", pid);

                                                                 const peer = connection.peers[pid];
                                                                 if (!peer) return;

                                                                 peer.peer.getReceivers().forEach((receiver: RTCRtpReceiver) => {
                                                                 if (receiver.track) {
                                                                     const stream = new MediaStream([receiver.track]);

                                                                     dispatch(addOrReplacePeer({
                                                                     id: pid,
                                                                     name: "Agent",
                                                                     avatar: "/avatars/02.png",
                                                                     isMuted: !receiver.track.enabled,
                                                                     isVideoOff: receiver.track.kind === "video" && !receiver.track.enabled,
                                                                     cameraStream: receiver.track.kind === "video" ? stream : null,
                                                                     screenShareStream: null,
                                                                     isScreenSharing: false,
                                                                     status: "connected"
                                                                     }));
                                                                 }
                                                                 });
                                                             });
                                                            }, 1500);

                                                            if (isRoomOpened === false && event.roomExist === true) {
                                                                console.log(`DEBUG: RTCMultiConnection: Joining existing room: ${assignedRoomId}`);
                                                                dispatch(addChatMessage({ sender: "System", message: `Joined existing room: ${assignedRoomId}`, timestamp: Date.now() }));
                                                            } else if (isRoomOpened === true) {
                                                                console.log(`DEBUG: RTCMultiConnection: Room opened: ${assignedRoomId}`);
                                                                dispatch(addChatMessage({ sender: "System", message: `Room opened: ${assignedRoomId}`, timestamp: Date.now() }));
                                                                // This is where chat messages from the RTC data channel will arrive.
                                                                connection.getSocket().on('messages', (senderIdFromRTC: string, chatEventData: any) => { 
                                                                    console.log("DEBUG: RTCMultiConnection Data Channel Message Received (raw):", chatEventData);

                                                                    // Determine sender name for display using peers from Redux state
                                                                    const senderName = localStorage.getItem("AgentName");

                                                                    if (chatEventData && typeof chatEventData.messageType === 'string') {
                                                                        switch (chatEventData.messageType) {
                                                                            case 'chat':
                                                                                if (chatEventData.typing === true) {
                                                                                    console.log(`DEBUG: ${senderName} is typing...`);
                                                                                    // You can dispatch an action here to update a 'typing' status in Redux
                                                                                } else if (chatEventData.typing === false) {
                                                                                    console.log(`DEBUG: ${senderName} stopped typing.`);
                                                                                    // e.g., dispatch(setTypingStatus({ userId: senderIdFromRTC, isTyping: false }));
                                                                                } else if (chatEventData.data) {
                                                                                    // Actual chat message
                                                                                    console.log(`DEBUG: RTCMultiConnection - Detected chat message from ${senderName} via Data Channel: "${chatEventData.data}"`);

                                                                                    dispatch(addChatMessage({
                                                                                        sender: senderName as string,
                                                                                        message: chatEventData.data,
                                                                                        timestamp: Date.now(),
                                                                                        messageType: 'chat' // Explicitly set messageType
                                                                                    }));
                                                                                    console.log("DEBUG: Dispatched addChatMessage for Data Channel message.");

                                                                                    // Trigger the callback to notify the UI (e.g., ChatBotPage) to scroll to bottom
                                                                                    if (onNewChatMessageReceivedFromRTC) {
                                                                                        onNewChatMessageReceivedFromRTC();
                                                                                    }
                                                                                }
                                                                                break;
                                                                            case 'whiteboard_event': // NEW: Handle whiteboard events
                                                                                console.log(`DEBUG: RTCMultiConnection - Detected whiteboard event from ${senderName}:`, chatEventData.data);
                                                                                dispatch(addWhiteboardEvent({
                                                                                    id: `${Date.now()}-${senderIdFromRTC}-${Math.random().toString(36).substring(2, 9)}`, // Generate unique ID for the event
                                                                                    type: chatEventData.data.action, // e.g., 'add_object', 'modify_object', 'clear_all'
                                                                                    data: chatEventData.data.data, // This is the FabricObjectData (JSON string)
                                                                                    senderId: senderIdFromRTC,
                                                                                    timestamp: Date.now(),
                                                                                }));
                                                                                break;
                                                                            case 'open_whiteboard': 
                                                                                console.log(`DEBUG: RTCMultiConnection - Detected open_whiteboard command from ${senderName}.`);
                                                                                dispatch(setIsWhiteboardOpen(true));
                                                                                // dispatch(clearAllWhiteboardEvents()); // Clear board on open
                                                                                dispatch(addChatMessage({ sender: "System", message: `${senderName} opened the whiteboard.`, timestamp: Date.now() }));
                                                                                break;
                                                                            case 'close_whiteboard': 
                                                                                console.log(`DEBUG: RTCMultiConnection - Detected close_whiteboard command from ${senderName}.`);
                                                                                dispatch(setIsWhiteboardOpen(false));
                                                                                // dispatch(clearAllWhiteboardEvents()); // Clear board on close
                                                                                dispatch(addChatMessage({ sender: "System", message: `${senderName} closed the whiteboard.`, timestamp: Date.now() }));
                                                                                break;
                                                                            case 'SE':
                                                                                console.log(`DEBUG: RTCMultiConnection - Detected Stream End (SE) message from ${senderName}.`);
                                                                                console.log("🛑 Agent explicitly ended the session");
                                                                                agentEndedSessionRef.current = true;

                                                                                dispatch(addChatMessage({
                                                                                  sender: "System",
                                                                                  message: "Agent ended the call.",
                                                                                  timestamp: Date.now()
                                                                                }));

                                                                                disconnect(); // ✅ FINAL DISCONNECT
                                                                                break;
                                                                            default:
                                                                                console.warn("DEBUG: RTCMultiConnection - Unhandled data channel messageType:", chatEventData.messageType, "from", senderName);
                                                                        }
                                                                    } else {
                                                                        console.warn("DEBUG: RTCMultiConnection - Unhandled data channel message format or missing messageType:", chatEventData);
                                                                    }
                                                                });
                                                            }
                                                        });
                                                        console.log(`DEBUG: RTCMultiConnection attempting to open/join room: ${assignedRoomId}`);
                                                        // connection.renegotiate();              
                                                    } else {
                                                        toast.error("RTC Server Error")
                                                        console.error("DEBUG: RTCMultiConnection library (window.RTCMultiConnection) is not defined. Make sure the CDN script is included in public/index.html BEFORE your React app bundle.");
                                                        dispatch(addChatMessage({ sender: "System", message: "Video call library missing. Please check application setup.", timestamp: Date.now() }));
                                                    }

                                                } else {
                                                    console.warn("DEBUG: Missing reqId or assignedUser for VCCStatServer/token API call. Skipping.");
                                                    dispatch(addChatMessage({ sender: "System", message: "Missing required data for call setup.", timestamp: Date.now() }));
                                                }
                                            } catch (error) {
                                                toast.error("Error calling VCCStatServer/token API")
                                                console.error("DEBUG: Error calling VCCStatServer/token API or initializing RTCMultiConnection:", error);
                                                // dispatch(addChatMessage({ sender: "System", message: `Call setup failed: ${error instanceof Error ? error.message : String(error)}.`, timestamp: Date.now() }));
                                            }
                                            break;
                                        case 'userJoined':
                                            console.log(`DEBUG: User ${hubMessage.A[0]} joined the room via SignalR.`);
                                            dispatch(addChatMessage({ sender: "System", message: `${hubMessage.A[0]} joined the call.`, timestamp: Date.now() }));
                                            break;
                                        case 'userLeft':
                                            console.log(`DEBUG: User ${hubMessage.A[0]} left the room via SignalR.`);
                                            dispatch(addChatMessage({ sender: "System", message: `${hubMessage.A[0]} left the call.`, timestamp: Date.now() }));
                                            break;
                                        case 'receiveChatMessage':
                                            // This case is for SignalR messages, but the 'messages' case below is more aligned with old code.
                                            console.log(`DEBUG: Main WS received 'receiveChatMessage'. This is likely for signaling, not direct chat content. Ignoring for chat display.`);
                                            break;
                                        case 'messages': 
                                            console.log("DEBUG: SignalR Main WS - Detected 'messages' event for chat.");
                                            const [senderIdFromSignalR, chatEventData] = hubMessage.A;

                                            if (chatEventData && chatEventData.messageType === 'chat') { // Check if it's a chat message
                                                if (chatEventData.typing === true) {
                                                    console.log(`DEBUG: ${senderIdFromSignalR} is typing...`);
                                                    // Optionally dispatch an action to show a typing indicator in your UI
                                                } else if (chatEventData.typing === false) {
                                                    console.log(`DEBUG: ${senderIdFromSignalR} stopped typing.`);
                                                } else {
                                                    // Actual chat message
                                                    const senderName = peers.find(p => p.id === senderIdFromSignalR)?.name || senderIdFromSignalR;
                                                    dispatch(addChatMessage({
                                                        sender: senderName,
                                                        message: chatEventData.data,
                                                        timestamp: Date.now(),
                                                        messageType: 'chat'
                                                    }));
                                                    console.log(`DEBUG: SignalR Main WS - Chat message received from ${senderName}: "${chatEventData.data}"`);
                                                    if (onNewChatMessageReceivedFromRTC) {
                                                        onNewChatMessageReceivedFromRTC(); // Trigger callback for UI update (e.g., scroll to bottom)
                                                    }
                                                }
                                            } else {
                                                console.warn("DEBUG: SignalR Main WS - Unhandled 'messages' event format or non-chat type:", chatEventData);
                                            }
                                            break;
                                        case 'UpdateStreamStatus':
                                            const [participantId, isVideoOff, isMicMuted, isScreenSharing] = hubMessage.A;
                                            console.log(`DEBUG: Received UpdateStreamStatus for ${participantId}: VideoOff=${isVideoOff}, MicMuted=${isMicMuted}, ScreenSharing=${isScreenSharing}`);
                                            // Use latestPeersRef to get the most up-to-date peer state
                                            const currentPeerState = latestPeersRef.current.find(p => p.id === participantId);

                                            dispatch(addOrReplacePeer({ // Use addPeer to update existing peer
                                                id: participantId,
                                                name: currentPeerState?.name || participantId, // Keep existing name
                                                avatar: currentPeerState?.avatar || "/avatars/02.png",
                                                isMuted: isMicMuted,
                                                isVideoOff: isVideoOff,
                                                cameraStream: currentPeerState?.cameraStream || null, // Keep existing camera stream
                                                screenShareStream: currentPeerState?.screenShareStream || null, // Keep existing screen share stream
                                                isScreenSharing: isScreenSharing, // Update screen sharing status
                                                status: 'connected'
                                            }));
                                            break;
                                        default:
                                            console.log("DEBUG: Unhandled hub message method:", hubMessage.M, "with arguments:", hubMessage.A);
                                    }
                                } else {
                                    console.warn(`DEBUG: Hub name mismatch: Expected '${HUB_NAME}', got '${hubMessage.H}' for method '${hubMessage.M}'`);
                                }
                            } else {
                                console.warn("DEBUG: Received hub message with missing H, M, or invalid format:", parsedMessage);
                            }
                        }
                    } else if (parsedMessage.C || parsedMessage.I) {
                        // console.log("Received SignalR control/invocation message:", parsedMessage);
                    } else {
                        // Removed previous handling of 42["messages", ...] here.
                        console.warn("DEBUG: Received unexpected SignalR message format from Main WS:", parsedMessage);
                    }
                } catch (parseError) {
                    toast.error("Failed to parse SignalR message JSON from Main WS")
                    console.error("DEBUG: Failed to parse SignalR message JSON from Main WS:", rawMessage, parseError);
                    dispatch(addChatMessage({ sender: "System", message: "Failed to process server message.", timestamp: Date.now() }));
                }
            };

            ws.onclose = (event) => {
                console.warn(`DEBUG: SignalR WebSocket closed. Code: ${event.code}, Reason: ${event.reason}, WasClean: ${event.wasClean}`);
                // Only dispatch disconnected if it's not a clean close (code 1000 - normal closure)
                if (!event.wasClean || event.code !== 1000) {
                    dispatch(setConnectionStatus(`disconnected`));
                    console.log("DEBUG: Dispatched setConnectionStatus('disconnected'). Current Redux connectionStatus:", connectionStatus); // Log after dispatch
                    if (rtcmConnectionRef.current) {
                        console.log("DEBUG: Closing RTCMultiConnection session due0 to SignalR WS close.");
                        rtcmConnectionRef.current.closeEntireSession(() => {
                            console.log("DEBUG: RTCMultiConnection session closed.");
                        });
                        rtcmConnectionRef.current = null;
                    }
                    connectionTokenRef.current = null;
                    connectionIdRef.current = null;
                    roomIdRef.current = null;
                    dispatch(addChatMessage({ sender: "System", message: "SignalR connection lost. Attempting to reconnect...", timestamp: Date.now() }));
                    if (pollingIntervalRef.current) {
                        clearInterval(pollingIntervalRef.current);
                        pollingIntervalRef.current = null;
                    }
                } else {
                    console.log("DEBUG: SignalR WebSocket closed cleanly.");
                }
            };

            ws.onerror = (error) => {
                console.error("DEBUG: SignalR WebSocket error:", error);
                dispatch(setConnectionStatus('error'));
                console.log("DEBUG: Dispatched setConnectionStatus('error') in onerror. Current Redux connectionStatus:", connectionStatus); // Log after dispatch
                dispatch(setIsCallActive(false));
                dispatch(setIsTokenAcquired(false));
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
                dispatch(addChatMessage({ sender: "System", message: "WebSocket error. See console for details.", timestamp: Date.now() }));
            };

            // Start GetInteractionData polling only if not already active
            if (!pollingIntervalRef.current) {
                pollingIntervalRef.current = setInterval(PostGetInteractionData, 5000);
                console.log("DEBUG: Started GetInteractionData polling.");
            }

        } catch (error) {
            console.error("DEBUG: SignalR connection error:", error);
            toast.error("SignalR connection error",)
            dispatch(setConnectionStatus('failed'));
            console.log("DEBUG: Dispatched setConnectionStatus('failed') in catch. Current Redux connectionStatus:", connectionStatus); // Log after dispatch
            dispatch(setIsTokenAcquired(false));
            dispatch(setIsCallActive(false));
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
            dispatch(addChatMessage({ sender: "System", message: "Connection failed. Check console for details.", timestamp: Date.now() }));
            return;
        }
    }, [userProvidedNameRef, lobbyStream, getLocalMedia, connectionStatus, sendSignalRMessage, PostGetInteractionData, socketURL, dispatch, localStream, localParticipant, activeSpeakerInfo, isTokenAcquired, isCallActive, selectedCamera, selectedMic, localMicOn, localVideoOn, screenShareActive, onNewChatMessageReceivedFromRTC, currentUserName, UserName, peers, availableMics, availableCameras]); // Added availableMics, availableCameras to dependencies
    
    const abortSignalRConnection = useCallback(async () => {
      try {
        if (!connectionTokenRef.current) {
          console.warn("DEBUG: No connectionTokenRef found — abort skipped.");
          return;
        }

        const connectionDataPayload = JSON.stringify([{ name: HUB_NAME }]);

        const abortParams = {
          transport: "webSockets",
          clientProtocol: "2.1",
          username: currentUserName,
          connectionToken: connectionTokenRef.current,
          connectionData: connectionDataPayload,
          _: Date.now(),
        };

        console.log("DEBUG: Calling SignalR /abort endpoint…");

        await axios.post(API_URL.ABORT_URL, null, {
          params: abortParams,
          withCredentials: true,
        });

        console.log("DEBUG: SignalR /abort completed successfully.");

    // 🔥 Close WebSocket if still open
        if (reconnectWsRef.current && reconnectWsRef.current.readyState === WebSocket.OPEN) {
          console.log("DEBUG: Closing WebSocket after abort.");
          reconnectWsRef.current.close();
          reconnectWsRef.current = null;
        }

        dispatch(setConnectionStatus("disconnected"));
        console.log("DEBUG: Connection status set to disconnected.");

      } catch (err) {
        console.error("DEBUG: Error while aborting SignalR connection:", err);
      }
    }, [
      currentUserName,
      HUB_NAME,
      dispatch,
    ]);
    // --- Reconnection Logic on Component Mount ---
    useEffect(() => {
        const session = localStorage.getItem('callSession');
        if (session) {
            const { roomId, userId, isCallActive, localMicOn: storedLocalMicOn, localVideoOn: storedLocalVideoOn, selectedMic: storedSelectedMic, selectedCamera: storedSelectedCamera } = JSON.parse(session);

            if (isCallActive && roomId && userId) {
                console.log("DEBUG: Attempting reconnection from localStorage session...");
                // Attempt reconnection
                rejoinExistingCall(roomId, userId, storedLocalMicOn, storedLocalVideoOn, storedSelectedMic, storedSelectedCamera);
            }
        } else {
            console.log("DEBUG: No call session found in localStorage. Not attempting reconnection.");
        }
    }, []);
const hydrateRemoteStreamsStable = useCallback((connection: any) => {
  if (hydrationTimerRef.current) {
    clearTimeout(hydrationTimerRef.current);
  }

  hydrationTimerRef.current = setTimeout(() => {
    const participants = connection.getAllParticipants?.() || [];
    if (!participants.length) return;

    console.log("🧩 Hydrating receivers:", participants);

    participants.forEach((pid: string) => {
      const peerObj = connection.peers?.[pid];
      if (!peerObj?.peer) return;

      const pc: RTCPeerConnection = peerObj.peer;
      const receivers = pc.getReceivers();

      // ✅ Accept first non-ended video track
      const videoReceiver = receivers.find(r =>
        r.track &&
        r.track.kind === "video" &&
        r.track.readyState !== "ended"
      );

      if (!videoReceiver?.track) {
        console.log("⏳ No video receiver yet for:", pid);
        return;
      }

      const cameraStream = new MediaStream([videoReceiver.track]);

      const existing = latestPeersRef.current.find(p => p.id === pid);
      const existingTrackId =
        existing?.cameraStream?.getVideoTracks?.()[0]?.id;

      // ✅ If same track already bound → skip
      if (existingTrackId === videoReceiver.track.id) return;

      console.log("🎥 Binding camera for:", pid);

      dispatch(addOrReplacePeer({
        id: pid,
        name: "Agent",
        avatar: "/avatars/02.png",
        isMuted: false,
        isVideoOff: false,
        cameraStream,
        screenShareStream: null,
        isScreenSharing: false,
        status: "connected"
      }));

      dispatch(setActiveSpeakerInfo({
        id: pid,
        name: "Agent",
        avatar: "/avatars/02.png",
        isMuted: false,
        isVideoOff: false,
        cameraStream,
        screenShareStream: null,
        isScreenSharing: false
      }));

      dispatch(setActiveSpeakerStream(cameraStream));
    });
  }, 800);
}, [dispatch]);

    // --- Rejoin Existing Call Function ---
    const rejoinExistingCall = useCallback(async (roomId: string, userIdFromSession: string, storedLocalMicOn: any, storedLocalVideoOn: any, storedSelectedMic: any, storedSelectedCamera: any) => {
        const reconnectedLocalStream = await getLocalMedia(storedLocalMicOn, storedLocalVideoOn, storedSelectedMic, storedSelectedCamera);

        if (reconnectedLocalStream && rtcmConnectionRef.current) {
            if (!rtcmConnectionRef.current.streams.includes(reconnectedLocalStream)) {
                rtcmConnectionRef.current.addStream(reconnectedLocalStream);
                console.log("DEBUG: Rejoining: Local camera stream explicitly re-added to RTCMultiConnection after openOrJoin:", reconnectedLocalStream);
            }
        }
        try {
            dispatch(setConnectionStatus('reconnecting'));
            dispatch(addChatMessage({ sender: "System", message: "Attempting to rejoin call...", timestamp: Date.now() }));

            const interactionToken = localStorage.getItem('InteractionToken');
            if (!interactionToken) {
                console.error("DEBUG: Rejoin failed: No InteractionToken found in localStorage.");
                dispatch(addChatMessage({ sender: "System", message: "Rejoin failed: Missing call token.", timestamp: Date.now() }));
                dispatch(setConnectionStatus('failed'));
                localStorage.removeItem('callSession');
                return;
            }

            const RTCMultiConnectionGlobal = window.RTCMultiConnection;
            if (typeof RTCMultiConnectionGlobal !== 'undefined') {
                console.log("DEBUG: Rejoining: Initializing RTCMultiConnection with its own Socket.IO connection...");
                const connection = new RTCMultiConnectionGlobal(roomId, null, null, interactionToken);
                connection.iceServers = [];
                connection.iceServers = window.AtHomeConfig.rtcConfig.iceServers;
                rtcmConnectionRef.current = connection;
                console.log("DEBUG: Rejoining: RTCMultiConnection instance created. Initial 'is' status:", connection.is);

                dispatch(setUserId(connection.userid || userIdFromSession));
                console.log("DEBUG: Rejoining: Redux userId updated to RTCMultiConnection's userid:", connection.userid || userIdFromSession);

                localStorage.setItem('callSession', JSON.stringify({
                    roomId,
                    userId: connection.userid,
                    isCallActive: true,
                    localMicOn: storedLocalMicOn,
                    localVideoOn: storedLocalVideoOn,
                    selectedMic: storedSelectedMic,
                    selectedCamera: storedSelectedCamera,
                    timestamp: Date.now()
                }));

                connection.autoCreateMediaElement = false;
                console.log("DEBUG: RTCMultiConnection.autoCreateMediaElement set to false.");
                connection.dontCaptureUserMedia = true;
                connection.socketURL = socketURL;
                console.log("DEBUG: RTCMultiConnection socketURL set to:", connection.socketURL);

                // Configure RTCMultiConnection session
                connection.session = {
                    audio: true,
                    video: true,
                    data: true 
                };
                connection.sdpConstraints.mandatory = {
                    OfferToReceiveAudio: true,
                    OfferToReceiveVideo: true
                };

                connection.extra = {
                    userName: currentUserName,
                    interactionToken: interactionToken 
                };
                
                connection.onUserStatusChanged = (event: any) => {
  console.log("DEBUG: RTCMultiConnection onUserStatusChanged:", event);
  if (agentEndedSessionRef.current) return;

  const newAgentId = event.userid;
  const agentName = event?.extra?.userFullName || "Agent";

  // ---- OFFLINE ----
  if (event.status === "offline") {
    console.log("⚠️ Agent went offline (temporary)");
    toast.info(`Agent ${agentName} disconnected. Reconnecting...`);
    dispatch(setConnectionStatus("ActiveConnecting"));
    return; 
  }

  // ---- ONLINE (RECONNECTED) ----
  if (event.status === "online") {

    // remove stale peer if any
    if (
      agentUserIdRef.current &&
      agentUserIdRef.current !== newAgentId
    ) {
      dispatch(removePeer(agentUserIdRef.current));
    }

    agentUserIdRef.current = newAgentId;

    // reset speaker
    dispatch(setActiveSpeakerInfo(null));
    dispatch(setActiveSpeakerStream(null));
    dispatch(setConnectionStatus("connected"));

    // 🔥 SINGLE renegotiation
    if (!isRenegotiatingRef.current) {
      isRenegotiatingRef.current = true;

      setTimeout(() => {
        try {
          console.log("🔁 Renegotiating with:", newAgentId);
          connection.renegotiate(newAgentId);
        } catch (e) {
          console.warn("renegotiate failed:", e);
        }

        setTimeout(() => {
          hydrateRemoteStreamsStable(connection);
          isRenegotiatingRef.current = false;
        }, 800);

      }, 500);
    }
  }
};

                
                connection.onstream = async (event: any) => {
                    // debugger;
                    console.log("DEBUG: RTCMultiConnection onstream event received:", event);
                      
                    const isScreenStream = event.stream.isScreen || // RTCMultiConnection specific flag
                        event.stream.getVideoTracks().some((track: MediaStreamTrack) => {
                            const label = track.label.toLowerCase();
                            const isLabelBased = label.includes('screen') || label.includes('display');
                            const settings = track.getSettings();
                            // Check for common properties indicating a display source
                            const isDisplaySurface = settings.displaySurface === 'monitor' ||
                                settings.displaySurface === 'window' ||
                                settings.displaySurface === 'application';
                            return isLabelBased || isDisplaySurface;
                        });
                    console.log(`DEBUG: Stream from ${event.userid} detected as Screen Stream: ${isScreenStream}`);

                    const existingPeer = latestPeersRef.current.find(p => p.id === event.userid);
                    const peerName = event.extra.userFullName ;
                    const peerAvatar = existingPeer?.avatar || "/avatars/02.png";

                    // Initialize updated properties based on existing peer state
                    let updatedCameraStream = existingPeer?.cameraStream || null;
                    let updatedScreenShareStream = existingPeer?.screenShareStream || null;
                    let updatedIsScreenSharing = existingPeer?.isScreenSharing || false;
                    let updatedIsMuted = existingPeer?.isMuted || !event.stream.getAudioTracks().some((t: MediaStreamTrack) => t.enabled);
                    let updatedIsVideoOff = existingPeer?.isVideoOff || !event.stream.getVideoTracks().some((t: MediaStreamTrack) => t.enabled);


                    if (event.type === 'local') {
                        if (isScreenStream) {
                            localScreenShareStreamRef.current = event.stream; // Store in ref
                            dispatch(setScreenShareActive(true)); // Update local flag
                            updatedScreenShareStream = event.stream;
                            updatedIsScreenSharing = true;
                            // For local screen share, camera stream remains localStream (from Redux)
                            updatedCameraStream = localStream; // Use Redux localStream for local camera
                            updatedIsVideoOff = !localVideoOn; // This should reflect the camera's video status
                            updatedIsMuted = !localMicOn; // This should reflect the camera's mic status
                            console.log("DEBUG: Local screen share stream set.");
                        } else { // Local camera stream
                            dispatch(setLocalStream(event.stream)); // This already updates localStream (camera)
                            updatedCameraStream = event.stream;
                            updatedIsVideoOff = !event.stream.getVideoTracks().some((t: MediaStreamTrack) => t.enabled);
                            updatedIsMuted = !event.stream.getAudioTracks().some((t: MediaStreamTrack) => t.enabled);
                            // Local screen share status and stream should be retained from refs/Redux
                            updatedScreenShareStream = localScreenShareStreamRef.current;
                            updatedIsScreenSharing = screenShareActive; // Use Redux screenShareActive for local
                            console.log("DEBUG: Local camera stream and peers for local user should now be updated.");
                        }
                    } else if (event.type === 'remote') {
                        if (isScreenStream) {
                            updatedScreenShareStream = event.stream;
                            updatedIsScreenSharing = true;
                            // For remote screen share, don't change cameraStream state if it exists
                            // Use existingPeer's cameraStream for retention
                            updatedCameraStream = existingPeer?.cameraStream || null;
                            updatedIsVideoOff = false; // Screen share implies video is not off
                            // Mute status for screen share (audio) should come from stream if available, or keep existing for consistency
                            updatedIsMuted = !event.stream.getAudioTracks().some((t: MediaStreamTrack) => t.enabled); // Check screen share stream's audio
                            console.log(`DEBUG: Remote screen share stream from ${event.userid} set.`);
                        } else { // Remote camera stream
                            updatedCameraStream = event.stream;
                            updatedIsVideoOff = !event.stream.getVideoTracks().some((t: MediaStreamTrack) => t.enabled);
                            updatedIsMuted = !event.stream.getAudioTracks().some((t: MediaStreamTrack) => t.enabled);
                            // For remote camera, don't change screenShareStream state if it exists
                            // Use existingPeer's screenShareStream for retention
                            updatedScreenShareStream = existingPeer?.screenShareStream || null;
                            updatedIsScreenSharing = existingPeer?.isScreenSharing || false; // Retain existing screen sharing status
                            console.log(`DEBUG: Remote camera stream from ${event.userid} set.`);
                        }
                    }
                    const stalePeers = latestPeersRef.current.filter(
                     p => p.name === peerName && p.id !== event.userid
                    );

                    stalePeers.forEach(p => {
                    console.log("🧹 Removing stale peer from Redux:", p.id);
                    dispatch(removePeer(p.id));
                    });


                    // Dispatch addPeer with the calculated updated streams and statuses
                    // This ensures the Redux 'peers' array is always up-to-date with the latest stream info.
                    dispatch(addOrReplacePeer({
                        id: event.userid,
                        name: peerName,
                        avatar: peerAvatar,
                        isMuted: updatedIsMuted,
                        isVideoOff: updatedIsVideoOff,
                        cameraStream: updatedCameraStream,
                        screenShareStream: updatedScreenShareStream,
                        isScreenSharing: updatedIsScreenSharing,
                        status: 'connected'
                    }));

                    // Active speaker logic:
                    // Prioritize screen share, then active camera streams.
                    if (isScreenStream && (!activeSpeakerInfo || !activeSpeakerInfo.isScreenSharing)) {
                        dispatch(setActiveSpeakerInfo({
                            id: event.userid,
                            name: peerName,
                            avatar: peerAvatar,
                            isMuted: updatedIsMuted,
                            isVideoOff: updatedIsVideoOff,
                            cameraStream: updatedCameraStream,
                            screenShareStream: updatedScreenShareStream,
                            isScreenSharing: updatedIsScreenSharing,
                        }));
                        dispatch(setActiveSpeakerStream(event.stream));
                        console.log(`DEBUG: Set active speaker to screen share from ${event.userid}.`);
                    } else if (!isScreenStream && (!activeSpeakerInfo || (!activeSpeakerInfo.isScreenSharing && activeSpeakerInfo.id !== event.userid))) {
                        // Only set as active speaker if no screen share is active or this is a new camera speaker
                        dispatch(setActiveSpeakerInfo({
                            id: event.userid,
                            name: peerName,
                            avatar: peerAvatar,
                            isMuted: updatedIsMuted,
                            isVideoOff: updatedIsVideoOff,
                            cameraStream: updatedCameraStream,
                            screenShareStream: updatedScreenShareStream,
                            isScreenSharing: updatedIsScreenSharing,
                        }));
                        dispatch(setActiveSpeakerStream(event.stream));
                        console.log(`DEBUG: Set active speaker to camera from ${event.userid}.`);
                    }

                    
                };
                
                connection.onstreamended = (event: any) => {
                    console.log("DEBUG: RTCMultiConnection onstreamended event:", event);

                    // *** REFINED: Use the same robust detection for ending streams ***
                    const isScreenStream = event.stream.isScreen ||
                        event.stream.getVideoTracks().some((track: MediaStreamTrack) => {
                            const label = track.label.toLowerCase();
                            const settings = track.getSettings();
                            const isLabelBased = label.includes('screen') || label.includes('display');
                            const isDisplaySurface = settings.displaySurface === 'monitor' ||
                                settings.displaySurface === 'window' ||
                                settings.displaySurface === 'application';
                            return isLabelBased || isDisplaySurface;
                        });
                    console.log(`DEBUG: Stream from ${event.userid} ended, detected as Screen Stream: ${isScreenStream}`);

                    // Use latestPeersRef to get the most up-to-date peer state
                    const existingPeer = latestPeersRef.current.find(p => p.id === event.userid);
                    if (!existingPeer) {
                        console.warn(`DEBUG: onstreamended: Peer ${event.userid} not found in state.`);
                        return;
                    }

                    let updatedCameraStream = existingPeer.cameraStream;
                    let updatedScreenShareStream = existingPeer.screenShareStream;
                    let updatedIsScreenSharing = existingPeer.isScreenSharing;
                    let updatedIsMuted = existingPeer.isMuted;
                    let updatedIsVideoOff = existingPeer.isVideoOff;

                    if (event.type === 'local') {
                        if (isScreenStream) {
                            localScreenShareStreamRef.current = null;
                            dispatch(setScreenShareActive(false));
                            updatedScreenShareStream = null;
                            updatedIsScreenSharing = false;
                            // Keep camera stream and its status as is
                            updatedCameraStream = localStream; // Retain local camera stream
                            updatedIsMuted = !localMicOn;
                            updatedIsVideoOff = !localVideoOn;
                            console.log("DEBUG: Local screen share stream ended.");
                        } else { // Local camera stream ended (e.g., device unplugged)
                            dispatch(setLocalStream(null)); // Clear localStream from Redux
                            updatedCameraStream = null; // Remove camera stream
                            updatedIsVideoOff = true; // Camera video is now off
                            // Keep screen share stream and its status as is
                            updatedScreenShareStream = localScreenShareStreamRef.current;
                            updatedIsScreenSharing = screenShareActive;
                            console.log("DEBUG: Local camera stream ended (device unplugged or stream stopped).");
                        }
                    } else if (event.type === 'remote') {
                        if (isScreenStream) {
                            updatedScreenShareStream = null;
                            updatedIsScreenSharing = false;
                            // Keep existing camera stream and its status
                            updatedCameraStream = existingPeer.cameraStream;
                            updatedIsMuted = existingPeer.isMuted;
                            updatedIsVideoOff = existingPeer.isVideoOff;
                            console.log(`DEBUG: Remote screen share stream from ${event.userid} ended.`);
                        } else { // Remote camera stream ended
                            updatedCameraStream = null;
                            updatedIsVideoOff = true;
                            updatedScreenShareStream = existingPeer.screenShareStream;
                            updatedIsScreenSharing = existingPeer.isScreenSharing;
                            updatedIsMuted = existingPeer.isMuted;
                            console.log(`DEBUG: Remote camera stream from ${event.userid} ended.`);
                        }
                    }


                    // Dispatch addPeer with the calculated updated streams and statuses
                    dispatch(addOrReplacePeer({
                        id: event.userid,
                        name: existingPeer.name,
                        avatar: existingPeer.avatar,
                        isMuted: updatedIsMuted,
                        isVideoOff: updatedIsVideoOff,
                        cameraStream: updatedCameraStream,
                        screenShareStream: updatedScreenShareStream,
                        isScreenSharing: updatedIsScreenSharing,
                        status: 'connected'
                    }));
                    // Active speaker logic:
                    // If the ended stream belonged to the active speaker, re-evaluate active speaker.
                    if (activeSpeakerInfo?.id === event.userid && activeSpeakerStream === event.stream) { // Check for exact stream match
                        let newActiveSpeaker: Participant | null = null;
                        let newActiveStream: MediaStream | null = null;

                        // Re-evaluate active speaker based on current state of peers
                        // Get the *latest* state of the peer whose stream just ended (after it's been updated in Redux)
                        const peerAfterUpdate = latestPeersRef.current.find(p => p.id === event.userid);

                        // Priority 1: If the *same user* whose screen share just ended now has an.
                        if (isScreenStream && peerAfterUpdate && peerAfterUpdate.cameraStream && !peerAfterUpdate.isVideoOff) {
                            newActiveSpeaker = peerAfterUpdate;
                            newActiveStream = peerAfterUpdate.cameraStream;
                            console.log(`DEBUG: Active speaker re-evaluated: Switched to camera of user ${event.userid} after screen share ended.`);
                        }
                        // Priority 2: Local Screen Share (if active)
                        else if (localScreenShareStreamRef.current && screenShareActive) {
                            newActiveSpeaker = { ...localParticipant, name: "Your Screen", isScreenSharing: true };
                            newActiveStream = localScreenShareStreamRef.current;
                            console.log("DEBUG: Active speaker re-evaluated: Switched to Local Screen Share.");
                        }
                        // Priority 3: Remote Screen Share (if any other is active)
                        else {
                            const remoteScreenSharingPeer = latestPeersRef.current.find(p => p.isScreenSharing && p.screenShareStream && p.id !== event.userid);
                            if (remoteScreenSharingPeer) {
                                newActiveSpeaker = remoteScreenSharingPeer;
                                newActiveStream = remoteScreenSharingPeer.screenShareStream;
                                console.log(`DEBUG: Active speaker re-evaluated: Switched to Remote Screen Share from ${remoteScreenSharingPeer.id}.`);
                            }
                            // Priority 4: Local Camera (if active)
                            else if (localStream && localVideoOn) {
                                newActiveSpeaker = localParticipant;
                                newActiveStream = localStream;
                            }
                            // Priority 5: Remote Camera (if any other is active)
                            else {
                                const remoteCameraPeer = latestPeersRef.current.find(p => p.cameraStream && !p.isVideoOff);
                                if (remoteCameraPeer) {
                                    newActiveSpeaker = remoteCameraPeer;
                                    newActiveStream = remoteCameraPeer.cameraStream;
                                }
                            }
                        }
                        dispatch(setActiveSpeakerInfo(newActiveSpeaker));
                        dispatch(setActiveSpeakerStream(newActiveStream));
                        console.log(`DEBUG: Active speaker re-evaluated after peer disconnected. New active speaker: ${newActiveSpeaker?.name || 'None'}`);
                    }
                };
                

                // Add onstatechange listener for more detailed debugging
                connection.onstatechange = (state: any) => {
                    console.log(`DEBUG: RTCMultiConnection state changed: ${state.name} (is: ${connection.is})`);
                    if (state.name === 'connected') {
                        console.log("DEBUG: RTCMultiConnection is now 'connected'.");
                    }
                };

                // RTCMultiConnection's openOrJoin will handle initial media acquisition.
                if (reconnectedLocalStream) {
                  console.log("DEBUG: Re-attaching local stream after reconnect");

                  connection.attachStreams.forEach((s: any) => {
                    connection.removeStream(s.streamid);
                  });

                  connection.addStream(reconnectedLocalStream);

                  // 🔥 Force renegotiation for all peers
                //   connection.getAllParticipants().forEach((pid: string) => {
                //     connection.renegotiate(pid);
                //   });
                }
                // Open or join the room
                connection.openOrJoin(roomId, (isRoomOpened: boolean, event: any) => {
                    console.log(`DEBUG: RTCMultiConnection openOrJoin callback. isRoomOpened: ${isRoomOpened}, event:`, event);
                    console.log("DEBUG: RTCMultiConnection current 'is' status inside openOrJoin callback:", connection.is);
                    // const interactionId = localStorage.getItem("requestID")
                    hydrateRemoteStreamsStable(connection);

                    // if (interactionId) {
                    //     connectPionRecordingCustomer(interactionId);
                    // }
                    if (connection.attachStreams.length > 1) {
                        console.warn(
                            "DEBUG: Multiple local streams detected, keeping the latest one."
                        );
                        // Remove duplicates
                        const firstStream = connection.attachStreams[0];
                        connection.attachStreams
                            .slice(1)
                            .forEach((s: any) => connection.removeStream(s.streamid));

                        // Replace tracks if needed
                        if (reconnectedLocalStream) {
                            console.log("DEBUG: Replacing tracks with stored local stream.");
                            if (firstStream.getAudioTracks()[0] && reconnectedLocalStream.getAudioTracks()[0]) {
                                connection.replaceTrack(
                                    reconnectedLocalStream.getAudioTracks()[0],
                                    firstStream.getAudioTracks()[0]
                                );
                            }
                            if (firstStream.getVideoTracks()[0] && reconnectedLocalStream.getVideoTracks()[0]) {
                                connection.replaceTrack(
                                    reconnectedLocalStream.getVideoTracks()[0],
                                    firstStream.getVideoTracks()[0]
                                );
                            }
                        }
                    }
                    setTimeout(() => {
                     const participants = connection.getAllParticipants();

                     participants.forEach((pid: string) => {
                         console.log("DEBUG: Checking remote streams for participant:", pid);

                         const peer = connection.peers[pid];
                         if (!peer) return;

                         peer.peer.getReceivers().forEach((receiver: RTCRtpReceiver) => {
                         if (receiver.track) {
                             const stream = new MediaStream([receiver.track]);

                             dispatch(addOrReplacePeer({
                             id: pid,
                             name: "Agent",
                             avatar: "/avatars/02.png",
                             isMuted: !receiver.track.enabled,
                             isVideoOff: receiver.track.kind === "video" && !receiver.track.enabled,
                             cameraStream: receiver.track.kind === "video" ? stream : null,
                             screenShareStream: null,
                             isScreenSharing: false,
                             status: "connected"
                             }));
                         }
                         });
                     });
                    }, 1500);

                    if (isRoomOpened === false && event.roomExist === true) {
                        console.log(`DEBUG: RTCMultiConnection: Joining existing room: ${roomId}`);
                        dispatch(addChatMessage({ sender: "System", message: `Joined existing room: ${roomId}`, timestamp: Date.now() }));
                    } else if (isRoomOpened === true) {
                        console.log(`DEBUG: RTCMultiConnection: Room opened: ${roomId}`);
                        dispatch(addChatMessage({ sender: "System", message: `Room opened: ${roomId}`, timestamp: Date.now() }));
                        // This is where chat messages from the RTC data channel will arrive.
                        connection.getSocket().on('messages', (senderIdFromRTC: string, chatEventData: any) => {
                            console.log("DEBUG: RTCMultiConnection Data Channel Message Received (raw):", chatEventData);

                            // Determine sender name for display using peers from Redux state
                            const senderName = localStorage.getItem("AgentName")

                            if (chatEventData && typeof chatEventData.messageType === 'string') {
                                switch (chatEventData.messageType) {
                                    case 'chat':
                                        if (chatEventData.typing === true) {
                                            console.log(`DEBUG: ${senderName} is typing...`);
                                            // e.g., dispatch(setTypingStatus({ userId: senderIdFromRTC, isTyping: true }));
                                        } else if (chatEventData.typing === false) {
                                            console.log(`DEBUG: ${senderName} stopped typing.`);
                                            // e.g., dispatch(setTypingStatus({ userId: senderIdFromRTC, isTyping: false }));
                                        } else if (chatEventData.data) {
                                            console.log(`DEBUG: RTCMultiConnection - Detected chat message from ${senderName} via Data Channel: "${chatEventData.data}"`);

                                            dispatch(addChatMessage({
                                                sender: senderName as string,
                                                message: chatEventData.data,
                                                timestamp: Date.now(),
                                                messageType: 'chat'
                                            }));
                                            console.log("DEBUG: Dispatched addChatMessage for Data Channel message.");

                                            // Trigger the callback to notify the UI (e.g., ChatBotPage) to scroll to bottom
                                            if (onNewChatMessageReceivedFromRTC) {
                                                onNewChatMessageReceivedFromRTC();
                                            }
                                        }
                                        break;
                                    case 'whiteboard_event':
                                        console.log(`DEBUG: RTCMultiConnection - Detected whiteboard event from ${senderName}:`, chatEventData.data);
                                        dispatch(addWhiteboardEvent({
                                            id: `${Date.now()}-${senderIdFromRTC}-${Math.random().toString(36).substring(2, 9)}`,
                                            type: chatEventData.data.action,
                                            data: chatEventData.data.data,
                                            senderId: senderIdFromRTC,
                                            timestamp: Date.now(),
                                        }));
                                        break;
                                    case 'open_whiteboard':
                                        console.log(`DEBUG: RTCMultiConnection - Detected open_whiteboard command from ${senderName}.`);
                                        dispatch(setIsWhiteboardOpen(true));
                                        // dispatch(clearAllWhiteboardEvents()); // Clear board on open
                                        dispatch(addChatMessage({ sender: "System", message: `${senderName} opened the whiteboard.`, timestamp: Date.now() }));
                                        break;
                                    case 'close_whiteboard':
                                        console.log(`DEBUG: RTCMultiConnection - Detected close_whiteboard command from ${senderName}.`);
                                        dispatch(setIsWhiteboardOpen(false));
                                        // dispatch(clearAllWhiteboardEvents()); // Clear board on close
                                        dispatch(addChatMessage({ sender: "System", message: `${senderName} closed the whiteboard.`, timestamp: Date.now() }));
                                        break;

                                    case 'SE':
                                        console.log(`DEBUG: RTCMultiConnection - Detected Stream End (SE) message from ${senderName}.`);
                                        console.log("🛑 Agent explicitly ended the session");
                                        agentEndedSessionRef.current = true;

                                        dispatch(addChatMessage({
                                          sender: "System",
                                          message: "Agent ended the call.",
                                          timestamp: Date.now()
                                        }));

                                        disconnect(); // ✅ FINAL DISCONNECT
                                        break;
                                    default:
                                        console.warn("DEBUG: RTCMultiConnection - Unhandled data channel messageType:", chatEventData.messageType, "from", senderName);
                                }
                            } else {
                                console.warn("DEBUG: RTCMultiConnection - Unhandled data channel message format or missing messageType:", chatEventData);
                            }
                        });
                    }
                    // connection.renegotiate();
                    dispatch(setIsCallActive(true));
                    dispatch(setConnectionStatus('connected'));
                    dispatch(addChatMessage({ sender: "System", message: "Successfully rejoined the call.", timestamp: Date.now() }));
                    console.log("DEBUG: Rejoining: Successfully rejoined call.");
                });

                console.log(`DEBUG: Rejoining: RTCMultiConnection attempting to open/join room: ${roomId}`);
            } else {
                console.error("DEBUG: Rejoining: RTCMultiConnection library (window.RTCMultiConnection) is not defined. Make sure the CDN script is included in public/index.html BEFORE your React app bundle.");
                dispatch(addChatMessage({ sender: "System", message: "Video call library missing. Please check application setup.", timestamp: Date.now() }));
                dispatch(setConnectionStatus('failed'));
                localStorage.removeItem('callSession');
            }
        } catch (error) {
            console.error("DEBUG: Reconnection failed:", error);
            toast.error("RTC server Failed")
            dispatch(setConnectionStatus('failed'));
            dispatch(addChatMessage({ sender: "System", message: `Reconnection failed: ${error instanceof Error ? error.message : String(error)}.`, timestamp: Date.now() }));
            localStorage.removeItem('callSession'); // Clear session on failure
        }
        
    }, [dispatch, socketURL, currentUserName, UserName, getLocalMedia, localMicOn, localVideoOn, selectedMic, selectedCamera, onNewChatMessageReceivedFromRTC, peers, localStream, localParticipant, activeSpeakerInfo, screenShareActive,]);

    // --- GetInteractionData Polling useEffect ---
    useEffect(() => {
        // This useEffect is now primarily for cleanup if polling was started elsewhere
        const session = localStorage.getItem('callSession');
        const isCallActive = session ? JSON.parse(session).isCallActive : false;
        return () => {
            if (!isCallActive) {
                if (pollingIntervalRef.current) {
                    console.log("DEBUG: Cleanup: Clearing GetInteractionData polling interval.");
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
            }
        };
    }, []);

    // --- Media Control Functions (now interacting with RTCMultiConnection) ---
    const toggleLocalMic = useCallback(() => {
        // If mic is currently on, just disable tracks.
        const currentSession = JSON.parse(localStorage.getItem('callSession') || '{}');
        const newLocalMicOnState = !localMicOn;
        if (localMicOn && localStream) {
            localStream.getAudioTracks().forEach(track => {
                track.enabled = false;
            });
            dispatch(setLocalMicOn(false));
            sendSignalRMessage({ H: HUB_NAME, M: 'UpdateStreamStatus', A: [userId, !localVideoOn, true, screenShareActive] });
        } else { // Mic is currently off, turn it on. Need to acquire/re-acquire stream.
            getLocalMedia(true, localVideoOn, selectedMic, selectedCamera);
            dispatch(setLocalMicOn(true));
            sendSignalRMessage({ H: HUB_NAME, M: 'UpdateStreamStatus', A: [userId, !localVideoOn, false, screenShareActive] });
        }
        if (currentSession.userId) { // Ensure a session exists
            const updatedSession = {
                ...currentSession,
                localMicOn: newLocalMicOnState,
                timestamp: Date.now()
            };
            localStorage.setItem('callSession', JSON.stringify(updatedSession));
            console.log("localStorage updated with new mic state:", updatedSession);
        }
    }, [localMicOn, localVideoOn, localStream, dispatch, userId, screenShareActive, sendSignalRMessage, getLocalMedia, selectedMic, selectedCamera]);


    const toggleLocalVideo = useCallback(() => {
        const currentSession = JSON.parse(localStorage.getItem('callSession') || '{}');
        const newLocalVideoOnState = !localVideoOn; // Calculate the new state


        // If video is currently on, just disable tracks.
        if (localVideoOn && localStream) {
            localStream.getVideoTracks().forEach(track => {
                track.enabled = false;
            });
            dispatch(setLocalVideoOn(false));
            sendSignalRMessage({ H: HUB_NAME, M: 'UpdateStreamStatus', A: [userId, true, !localMicOn, screenShareActive] });
        } else { // Video is currently off, turn it on. Need to acquire/re-acquire stream.
            getLocalMedia(localMicOn, true, selectedMic, selectedCamera);
            dispatch(setLocalVideoOn(true));
            sendSignalRMessage({ H: HUB_NAME, M: 'UpdateStreamStatus', A: [userId, false, !localMicOn, screenShareActive] });
        }
        if (currentSession.userId) { // Ensure a session exists
            const updatedSession = {
                ...currentSession,
                localVideoOn: newLocalVideoOnState,
                timestamp: Date.now()
            };
            localStorage.setItem('callSession', JSON.stringify(updatedSession));
            console.log("localStorage updated with new video state:", updatedSession);
        }
    }, [localVideoOn, localMicOn, localStream, dispatch, userId, screenShareActive, sendSignalRMessage, getLocalMedia, selectedMic, selectedCamera]);


    const toggleScreenShare = useCallback(async () => {
        if (!rtcmConnectionRef.current || !userId) {
            console.warn("DEBUG: RTCMultiConnection not initialized or userId missing for screen share.");
            dispatch(addChatMessage({ sender: "System", message: "Cannot share screen: Call not ready.", timestamp: Date.now() }));
            return;
        }

        if (screenShareActive) {
            // --- Stopping screen share ---
            console.log("DEBUG: Stopping screen share...");
            const screenShareStream = localScreenShareStreamRef.current; // Use the ref to get the stream
            if (screenShareStream) {
                screenShareStream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
                rtcmConnectionRef.current.removeStream(screenShareStream);
                localScreenShareStreamRef.current = null; // Clear the ref
                console.log("DEBUG: Screen share stream removed from RTCMultiConnection.");
            }

            dispatch(setScreenShareActive(false)); // Dispatch action to update Redux state for screen share

            // If the local camera was intended to be on before screen sharing, re-add it.
            if (localVideoOn) { 
                console.log("DEBUG: Screen share stopped. Attempting to restore local camera stream.");
                const restoredCameraStream = await getLocalMedia(localMicOn, true, selectedMic, selectedCamera);
                // Ensure the video track is enabled if getLocalMedia was successful and localVideoOn is true
                if (restoredCameraStream) { 
                    restoredCameraStream.getVideoTracks().forEach(track => track.enabled = true);
                }
            } else {
                if (localStream) { 
                    localStream.getTracks().forEach(track => track.stop());
                    dispatch(setLocalStream(null)); 
                }
            }

            // Inform others about stream status change via SignalR
            sendSignalRMessage({
                H: HUB_NAME,
                M: 'UpdateStreamStatus',
                A: [userId, !localVideoOn, !localMicOn, false] // isScreenSharing: false
            });
            dispatch(addChatMessage({ sender: "System", message: "Screen sharing stopped.", timestamp: Date.now() }));

        } else {
            // --- Starting screen share ---
            try {
                console.log("DEBUG: Starting screen share...");
                const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
                if (screenStream) {
                    localScreenShareStreamRef.current = screenStream;
                    // Add screen stream to RTCMultiConnection
                    // Add a small delay to ensure previous removeStream has processed
                    await new Promise(resolve => setTimeout(resolve, 50)); // Small delay
                    rtcmConnectionRef.current.addStream(screenStream);
                    console.log("DEBUG: Screen stream added to RTCMultiConnection:", screenStream);

                    dispatch(setScreenShareActive(true)); // Update Redux state

                    // Update local participant's state in Redux
                    dispatch(addOrReplacePeer({
                        id: userId,
                        name: UserName,
                        avatar: "/avatars/01.png",
                        isMuted: !localMicOn, // Mic status remains as is
                        isVideoOff: false, // Video is now active (screen share)
                        cameraStream: localStream, // Keep reference to camera stream
                        screenShareStream: screenStream,
                        isScreenSharing: true,
                    }));
                    // const micStream = await navigator.mediaDevices.getUserMedia({
                    //     audio: {
                    //        deviceId: selectedMic ? { exact: selectedMic } : undefined,
                    //     },
                    //     video: false,
                    // });
                    // const combinedStream = new MediaStream();
                    // screenStream.getVideoTracks().forEach(track => combinedStream.addTrack(track));

                    // // Add screen audio track (if any, e.g., system sound)
                    // screenStream.getAudioTracks().forEach(track => combinedStream.addTrack(track));

                    // // Add mic audio track
                    // micStream.getAudioTracks().forEach(track => combinedStream.addTrack(track))
                    // Listen for screen share end event
                    screenStream.getVideoTracks()[0].onended = () => {
                        console.log("DEBUG: Screen share ended by user.");
                        toggleScreenShare(); // Trigger stop logic
                    };
                    // if (rtcmConnectionRef.current) {
                    //     console.log("DEBUG: Adding screen share stream to RTCMultiConnection...");
                    //     rtcmConnectionRef.current.replaceTrack(combinedStream); // Correct RTCMultiConnection API
                    // } else {
                    //     console.error("DEBUG: rtcmConnectionRef.current is missing");
                    // }
                    // Inform others about stream status change via SignalR
                    sendSignalRMessage({
                        H: HUB_NAME,
                        M: 'UpdateStreamStatus',
                        A: [userId, false, !localMicOn, true] // isVideoOff: false (screen is video), isScreenSharing: true
                    });
                    dispatch(addChatMessage({ sender: "System", message: "Screen sharing started.", timestamp: Date.now() }));
                }
            } catch (error) {
                console.error("DEBUG: Error starting screen share:", error);
                toast.info("screen share is not possible")
                dispatch(setScreenShareActive(false));
                localScreenShareStreamRef.current = null;
                if (error instanceof DOMException && error.name === "NotAllowedError") {
                    dispatch(addChatMessage({ sender: "System", message: "Screen sharing denied. Please grant permission.", timestamp: Date.now() }));
                } else {
                    dispatch(addChatMessage({ sender: "System", message: `Failed to start screen sharing: ${error instanceof Error ? error.message : String(error)}.`, timestamp: Date.now() }));
                }
            }
        }
    }, [screenShareActive, dispatch, rtcmConnectionRef, userId, currentUserName, localMicOn, localVideoOn, localStream, sendSignalRMessage, getLocalMedia, selectedMic, selectedCamera]);


    const handleDeviceChange = useCallback(async (
        deviceId: string,
        type: "audio" | "video"
    ) => {
        console.log(`DEBUG: Device change requested: type=${type}, deviceId=${deviceId}`);

        if (type === "audio") {
            dispatch(setSelectedMic(deviceId));
            // Request audio with new deviceId, keep current video status and selected camera
            await getLocalMedia(localMicOn, localVideoOn, deviceId, selectedCamera);
        } else {
            dispatch(setSelectedCamera(deviceId));
            // Request video with new deviceId, keep current audio status and selected mic
            await getLocalMedia(localMicOn, localVideoOn, selectedMic, deviceId);
        }

        console.log(`DEBUG: Device changed: ${type} to ${deviceId}`);
        dispatch(addChatMessage({ sender: "System", message: `${type === 'audio' ? 'Microphone' : 'Camera'} changed.`, timestamp: Date.now() }));
    }, [getLocalMedia, dispatch, selectedMic, selectedCamera, localMicOn, localVideoOn]);

    const handleFacingModeChange = useCallback(
        (facingMode: "user" | "environment") => {
            console.log(`DEBUG: Facing mode change requested: ${facingMode}`);
            // When changing facing mode, explicitly request video with the new facingMode
            // and keep the current mic status and selected mic.
            // Do NOT pass selectedCamera as a deviceId constraint here, as facingMode overrides deviceId.
            getLocalMedia(localMicOn, true, selectedMic, undefined, facingMode);
            dispatch(setLocalVideoOn(true)); // Ensure video is on when changing facing mode
            dispatch(addChatMessage({ sender: "System", message: `Camera facing mode changed to ${facingMode}.`, timestamp: Date.now() }));
        },
        [getLocalMedia, selectedMic, localMicOn, dispatch]
    );

    // --- Disconnect Logic ---
    const disconnect = useCallback(() => {
        console.log("DEBUG: Disconnecting...");
         if (rtcmConnectionRef.current && rtcmConnectionRef.current.socket) {
        console.log("DEBUG: Sending SESSIONEND message via RTCMultiConnection socket.");
        try {
            // Assuming MESSAGE_TYPE, currentUserName, and roomIdRef.current are available in scope
            const roomNumber = roomIdRef.current; // Use the room ID from the ref
            const username = currentUserName;    // Use the current user name

            rtcmConnectionRef.current.socket.emit('messages', {
                message_type: MESSAGE_TYPE.SESSIONEND, // Ensure MESSAGE_TYPE is defined
                username: username,
                content: "Session Ended By Customer",
                room: roomNumber,
            });
            console.log("DEBUG: SESSIONEND message sent.");
        } catch (e) {
            console.error("DEBUG: Error sending SESSIONEND message on RTCMultiConnection socket:", e);
        }
    }
        // Close RTCMultiConnection session
        if (rtcmConnectionRef.current) {
            console.log("DEBUG: Closing RTCMultiConnection session.");
            rtcmConnectionRef.current.closeEntireSession(() => {
                console.log("DEBUG: RTCMultiConnection session closed.");
            });
            rtcmConnectionRef.current = null;
        }

        // Close main SignalR WebSocket (if still active)
        if (reconnectWsRef.current && reconnectWsRef.current.readyState === WebSocket.OPEN) {
            console.log("DEBUG: Sending leaveRoom to SignalR hub and closing SignalR WS.");
            if (roomIdRef.current && connectionTokenRef.current) {
                try {
                    reconnectWsRef.current.send(JSON.stringify({
                        C: connectionTokenRef.current,
                        M: [{ H: HUB_NAME, M: 'leaveRoom', A: [roomIdRef.current, currentUserName] }]
                    }));
                } catch (e) {
                    console.error("DEBUG: Error sending leaveRoom message on SignalR WS during disconnect:", e);
                }
            }
            reconnectWsRef.current.close(1000, "User disconnected.");
            reconnectWsRef.current = null;
        }

        // Stop local screen share stream if active
        if (localScreenShareStreamRef.current) {
            localScreenShareStreamRef.current.getTracks().forEach(track => track.stop());
            localScreenShareStreamRef.current = null;
        }

        // Stop local camera stream tracks explicitly if still active
        // This is vital for releasing camera/mic resources, especially on mobile.
        if (localStream) {
            localStream.getTracks().forEach(track => track.stop());
            console.log("DEBUG: Local camera stream tracks stopped.");
        }
        if (lobbyStream) {
            lobbyStream.cameraStream?.getTracks().forEach(track => track.stop());
            console.log("DEBUG: Lobby stream tracks stopped.");
        }
        dispatch(setLocalMicOn(false));
        dispatch(setLocalVideoOn(false));

        // Clear call session from localStorage on intentional disconnect
        localStorage.removeItem('callSession');
        localStorage.removeItem('InteractionToken');
        // Reset all call-related states in Redux
        dispatch(resetCallState());
        console.log("DEBUG: All states reset on disconnect.");
        dispatch(addChatMessage({ sender: "System", message: "Call ended.", timestamp: Date.now() }));

        // Navigate to the feedback page
        console.log("DEBUG: Navigating to /feedback page.");
        navigate('/UserFeedback');
        localStorage.clear()
    }, [dispatch, currentUserName, localStream, lobbyStream, navigate]);
    
    useEffect(() => {
      const connection = rtcmConnectionRef.current;

      if (!connection || !connection.socket) {
        return;
      }

      console.log("DEBUG: Setting up RTCMultiConnection socket listeners.");

      // --- Listener 1: RTCMultiConnection-Message (userLeft) ---
      const handleRemoteUserLeftMessage = (data: any) => {
        if (data?.message?.userLeft) {
          console.log(
            "⚠️ Agent temporarily left (userLeft). Waiting for reconnect."
          );

          // ❌ DO NOT disconnect unless agent explicitly ended session
          if (!agentEndedSessionRef.current) {
            dispatch(setConnectionStatus("ActiveConnecting"));
          }
        }
      };

      // --- Listener 2: user-disconnected ---
      const handleUserDisconnected = (userId: any) => {
        console.log(
          "⚠️ Agent socket disconnected:",
          userId,
          "(temporary)"
        );

        // ❌ DO NOT disconnect unless agent explicitly ended session
        if (!agentEndedSessionRef.current) {
          dispatch(setConnectionStatus("ActiveConnecting"));
        }
      };

      connection.socket.on(
        "RTCMultiConnection-Message",
        handleRemoteUserLeftMessage
      );
      connection.socket.on("user-disconnected", handleUserDisconnected);

      return () => {
        if (connection.socket) {
          console.log(
            "DEBUG: Cleaning up RTCMultiConnection socket listeners."
          );
          connection.socket.off(
            "RTCMultiConnection-Message",
            handleRemoteUserLeftMessage
          );
          connection.socket.off(
            "user-disconnected",
            handleUserDisconnected
          );
        }
      };
    }, [dispatch]);
    return {
        connect,
        disconnect,
        sendMessage,
        toggleLocalMic,
        toggleLocalVideo,
        toggleScreenShare,
        handleDeviceChange,
        handleFacingModeChange,
        areStreamsReady,
        sendRtcChatMessage,
        sendRtcDataMessage,
        InteractionData : interactionData,
        EstimedWaitTime : EstimedWaitTime,
        abortSignalRConnection,
    };
};